import { PLATFORM_TIMEZONE } from "../../../utils/timezone";
import { useState, useMemo, useEffect, useRef } from "react";
import { Check, Filter, Search, Trash2, Upload } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__lab_appointment_detail.module.scss";
import dialogStyles from "../../../styles/__labDialogAddEditTest.module.scss";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import { IoMdSettings } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { FaClock, FaFileInvoice, FaInfoCircle } from "react-icons/fa";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import { RxChevronDown, RxClock } from "react-icons/rx";
import { FiDownload, FiPrinter } from "react-icons/fi";
import {
  FaArrowLeftLong,
  FaCirclePlus,
  FaLocationArrow,
  FaPlus,
} from "react-icons/fa6";
import IconFileLargeView from "../../../assets/images/file-large-view.svg";
import reportPreview from "../../../assets/images/reportPreview.png";
import IconPDF from "../../../assets/images/IconPDF.png";
import UserAvatar from "../../../assets/images/patientAvatar.png";
import { BsFillChatTextFill } from "react-icons/bs";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "bootstrap";
import { LiaMapMarkerSolid } from "react-icons/lia";
import Chat from "../../../components/chat/ChatModal";
import { IoIosCheckbox } from "react-icons/io";
import useRejectionReasons, {
  CUSTOM_REJECTION_REASON,
} from "../../../hooks/useRejectionReasons";
import { AiOutlineFilePdf } from "react-icons/ai";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import LabInvoiceModal from "../../../components/lab-invoice-modal/LabInvoiceModal";
import axios from "axios";
import { APP_PATH } from "../../../api/backendPath";
import { toast } from "../../../components/toast";
import {
  getLabOrderDetails,
  generateLabOrderInvoice,
  addLabOrderTests,
  scheduleLabOrderAppointment,
  getAvailableTechnicians,
  assignLabOrderTechnician,
  getLabServiceFeesConfiguration,
} from "../../../services/labOrdersService";
import { getLabServices } from "../../../services/labTestsService";
import TestSearchSelect from "../../../components/test-search-select/TestSearchSelect";
import AmountInput from "../../../components/amount-input/AmountInput";
import {
  formatPercent,
  formatWholeAmount,
} from "../../../utils/amountFormat";
import SlaTimer from "../../../components/sla-timer/SlaTimer";
import {
  isChildBooking,
  getBookingChildDob,
  getBookingChildGender,
  ageFromDob,
  titleCaseGender,
} from "../../../utils/childBooking";
import { formatCustom as tzCustom, formatDateNumeric as tzDateNumeric, formatDateTimeNumeric as tzDateTimeNumeric, formatCustomInLocale as tzLocale, nowInDisplayZone } from "../../../utils/datetime";

// Canonical location codes for the lab order flow. Kept as constants so the
// values aren't hardcoded as magic strings throughout the component.
const LOCATION_CODES = {
  HOME_VISIT: "HOME_VISIT",
  ON_SITE: "ON_SITE",
};

// Default transportation fee (IQD) applied when a test's location is a home visit.
const HOME_VISIT_DEFAULT_TRANSPORTATION_FEE = "20";

// The preferred/service location can arrive in several shapes from the API
// (e.g. "Home Visit" / "HOME_VISIT" or "Visit Center" / "On-Site" / "ON_SITE" /
// "VISIT_CENTER"). Normalize any of them to a single canonical location code.
const normalizeLocationCode = (value) => {
  if (!value) return LOCATION_CODES.ON_SITE;
  const normalized = String(value)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "HOME_VISIT" || normalized === "HOMEVISIT"
    ? LOCATION_CODES.HOME_VISIT
    : LOCATION_CODES.ON_SITE;
};

// The provider's consultation type (configured by admin) constrains which test
// locations can be chosen: Online (video) → On-Site only, Home Visit (onsite) →
// Home Visit only, Both (video-onsite) → both. Unknown/empty → both (backward
// compatible so existing orders are never blocked).
const allowedLocationsFromConsultation = (type) => {
  const key = type ? String(type).trim().toLowerCase() : "";
  if (key === "video" || key === "online") return [LOCATION_CODES.ON_SITE];
  if (key === "onsite" || key === "home_visit" || key === "homevisit")
    return [LOCATION_CODES.HOME_VISIT];
  return [LOCATION_CODES.ON_SITE, LOCATION_CODES.HOME_VISIT];
};

const LabAppointmentDetail = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const { id: urlOrderId } = useParams(); // Get order ID from URL params
  const navigate = useNavigate(); // Add navigation hook

  // Get source parameter from URL to determine where to navigate back
  const searchParams = new URLSearchParams(window.location.search);
  const source = searchParams.get("source") || "ordermanagement"; // Default to order management

  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Order Details");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);

  // Service date and time state
  const [serviceDate, setServiceDate] = useState(null);
  const [serviceTime, setServiceTime] = useState("");
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const [gender, setGender] = useState("");

  // State for admin fee and consultation fee percentages
  // Default 0: no admin fee is applied unless the admin panel has an active
  // admin fee configured for the lab service.
  const [adminFeePercentage, setAdminFeePercentage] = useState(0);
  const [consultationFeePercentage, setConsultationFeePercentage] = useState(0); // Default fallback

  // Provider's consultation type (configured by admin) — constrains the test
  // location options. Null until fetched → both locations allowed.
  const [consultationType, setConsultationType] = useState(null);
  const allowedLocations = allowedLocationsFromConsultation(consultationType);

  // Order details state
  const [orderDetails, setOrderDetails] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [labCenterProfile, setLabCenterProfile] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  // Test management state
  const [tests, setTests] = useState([]);
  const [currentTest, setCurrentTest] = useState({
    testName: "",
    serviceCode: "",
    price: "",
    location: "ON_SITE",
    transportationFee: "0",
  });
  const [isSavingTests, setIsSavingTests] = useState(false);

  // Whether this order is On-Site vs Home Visit. Source of truth is the TEST
  // locations (there is no split anymore, so all tests share one location): if no
  // test is a HOME_VISIT the order is On-Site — in which case we skip technician
  // assignment/dispatch entirely and show the on-site "mark completed" workflow.
  // Falls back to the order's preferred location only when no tests exist yet.
  const isOnSiteOrder = useMemo(() => {
    const displayTests =
      tests && tests.length > 0 ? tests : orderDetails?.tests || [];
    if (displayTests.length > 0) {
      return displayTests.every(
        (t) => (t.location || "ON_SITE") !== "HOME_VISIT",
      );
    }
    const loc = orderDetails?.preferredLocation;
    return (
      loc === "Visit Center" || loc === "ON_SITE" || loc === "VISIT_CENTER"
    );
  }, [tests, orderDetails]);

  // Lab services state
  const [labServices, setLabServices] = useState([]);
  const [loadingLabServices, setLoadingLabServices] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState("");
  const [showAddTestRow, setShowAddTestRow] = useState(false);

  // Edit test state
  const [editingTestId, setEditingTestId] = useState(null);
  const [editingTest, setEditingTest] = useState({
    testName: "",
    serviceCode: "",
    price: "",
    location: "ON_SITE",
    transportationFee: "0",
  });
  const [editingTestServiceId, setEditingTestServiceId] = useState("");

  // Technician management state
  const [availableTechnicians, setAvailableTechnicians] = useState([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");
  // Loading state for the "Send Patient Details via WhatsApp" (assign technician) button
  const [assigningTechnician, setAssigningTechnician] = useState(false);
  // Loading states for the on-site Order Actions buttons (Sample Collected /
  // Mark as Collected-Completed / Job Done) so each shows an in-button spinner.
  const [isSampleCollecting, setIsSampleCollecting] = useState(false);
  const [isCompletingJob, setIsCompletingJob] = useState(false);

  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Accept / Reject (first-received order) modal + loading state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acceptingOrder, setAcceptingOrder] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(false);

  // Reject reason dropdown — admin-configured reasons for this center, plus an
  // "Other" option that reveals the free-text box. Fetched when the modal opens.
  const [selectedRejectReasonId, setSelectedRejectReasonId] = useState("");
  const {
    options: rejectReasonOptions,
    loading: loadingRejectReasons,
    resolveReasonText,
  } = useRejectionReasons("lab", showRejectModal);
  const isCustomRejectReason =
    selectedRejectReasonId === CUSTOM_REJECTION_REASON;

  // Single place to clear the dialog's inputs, used by every close path.
  const resetRejectForm = () => {
    setSelectedRejectReasonId("");
    setRejectReason("");
  };

  // Order-level transportation fee state
  const [orderTransportationFee, setOrderTransportationFee] = useState("0");
  const [transportationFeeAdded, setTransportationFeeAdded] = useState(false);
  // True once the lab has explicitly saved a 0 IQD fee in this view. The stored column defaults
  // to 0, so a refetch alone cannot tell "free transport" from "not entered yet"; this keeps an
  // explicit 0 from being reset to "not added" when the order is re-synced.
  const explicitZeroFeeRef = useRef(false);
  const [showTransportationFeePopup, setShowTransportationFeePopup] = useState(false);
  const [tempTransportationFee, setTempTransportationFee] = useState("0");

  // Use urlOrderId directly from useParams instead of storing in state
  const orderId = urlOrderId;

  // ===== Lab center operating-hours slots =====
  // Slots for the selected service date come from
  // GET /api/v1/lab/center-slots (center resolved from the JWT).
  // Service-date-based slots — drives "Center Availability" + the Service Time
  // options (recomputed whenever the officer changes the service date).
  const [centerSlots, setCenterSlots] = useState(null);
  // Preferred-date-based slots — drives ONLY the Preferred Time Slot dot/range,
  // pinned to the patient's original preferred date (does NOT change with the
  // selected service date).
  const [preferredSlots, setPreferredSlots] = useState(null);
  // Signature of the availability banner the user dismissed, so it stays hidden
  // for that exact situation but re-appears when the reason/date changes.
  const [dismissedBannerKey, setDismissedBannerKey] = useState(null);
  // Whether the "no time slots for this date" tooltip is dismissed (reset when
  // the officer picks a different service date).
  const [serviceTimeTipDismissed, setServiceTimeTipDismissed] = useState(false);
  // True only when the officer actively picks a service date, so the "closed"
  // toast fires on selection but not on the initial page load.
  const dateSelectedByUserRef = useRef(false);

  // Period bands (minutes since midnight) matching the backend slot buckets.
  const PERIOD_BANDS = {
    MORNING: { start: 0, end: 12 * 60 },
    AFTERNOON: { start: 12 * 60, end: 17 * 60 },
    EVENING: { start: 17 * 60, end: 24 * 60 },
  };

  const hhmmToMinutes = (value) => {
    if (!value) return NaN;
    const [h, m] = String(value).split(":").map(Number);
    if (isNaN(h)) return NaN;
    return h * 60 + (isNaN(m) ? 0 : m);
  };

  const minutesToHHMM = (mins) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

  const minutesToLabel12 = (mins) => {
    const norm = ((mins % 1440) + 1440) % 1440;
    const h24 = Math.floor(norm / 60);
    const m = norm % 60;
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  // Format a raw "HH:mm[:ss]" time string into a 12-hour label
  // (e.g. "10:00:00" → "10:00 AM"). Returns the original value if unparseable.
  const formatTime12h = (timeStr) => {
    if (!timeStr) return null;
    const [hStr, mStr] = String(timeStr).split(":");
    const h24 = Number(hStr);
    const m = Number(mStr) || 0;
    if (Number.isNaN(h24)) return timeStr;
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  const formatDateYMD = (dateObj) => {
    if (!dateObj) return null;
    const y = dateObj.getFullYear();
    const mo = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  };

  // Derive the canonical period key (MORNING/AFTERNOON/EVENING) from the
  // preferred time slot label — which may be a plain code ("MORNING") or a
  // full label such as "Morning (8:00 AM - 12:00 PM)".
  const getPreferredPeriodKey = () => {
    const slot = String(orderDetails?.preferredTimeSlot || "").toUpperCase();
    if (slot.includes("MORNING")) return "MORNING";
    if (slot.includes("AFTERNOON")) return "AFTERNOON";
    if (slot.includes("EVENING")) return "EVENING";
    return null;
  };

  // Normalize the patient's preferred date to YYYY-MM-DD for the center-slots
  // request (the field may be an ISO/date string).
  const getPreferredDateYMD = () => {
    const pd = orderDetails?.preferredDate;
    if (!pd) return null;
    const d = new Date(pd);
    return isNaN(d.getTime()) ? null : formatDateYMD(d);
  };

  // Parse a YYYY-MM-DD (or ISO) date string as a LOCAL calendar date so the day never
  // shifts with the browser's timezone. Null when it can't be read.
  const parseYMDLocal = (value) => {
    if (!value || value === "null") return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  // The service can be on the patient's preferred date or any later day. The calendar
  // starts at the preferred date, or at today (Baghdad) once the preferred date has passed.
  const earliestServiceDate = (preferredValue) => {
    const preferred = parseYMDLocal(preferredValue);
    const today = parseYMDLocal(nowInDisplayZone().date);
    return preferred && preferred > today ? preferred : today;
  };
  const preferredServiceDate = parseYMDLocal(orderDetails?.preferredDate);
  const minServiceDate = earliestServiceDate(orderDetails?.preferredDate);
  // True when `d` is before the preferred date. The date already saved on the order is
  // accepted as-is (the backend applies the same rule).
  const isBeforePreferredDate = (d) =>
    !!(d && preferredServiceDate) &&
    formatDateYMD(d) < formatDateYMD(preferredServiceDate) &&
    formatDateYMD(d) !==
      String(orderDetails?.scheduledDate || "").slice(0, 10);

  // Preferred-slot window for the patient's preferred period, clipped to the
  // center's operating hours ON THE PREFERRED DATE (pinned — not affected by the
  // selected service date). Ends before the closing time (band end),
  // e.g. Morning + 8AM–1PM hours => 8:00 AM – 12:00 PM.
  const getSlotWindowFrom = (slotsData) => {
    const slotKey = getPreferredPeriodKey();
    const band = slotKey ? PERIOD_BANDS[slotKey] : null;
    if (!band) return null;
    if (!slotsData || slotsData.is_open === false) return null;
    const oh = slotsData.operating_hours;
    if (!oh) return null;
    const start = Math.max(band.start, hhmmToMinutes(oh.start));
    const end = Math.min(band.end, hhmmToMinutes(oh.end));
    if (!(start < end)) return null;
    return { startMin: start, endMin: end };
  };

  // Preferred-slot window pinned to the PREFERRED date (not affected by the
  // selected service date).
  const getPreferredSlotWindow = () => getSlotWindowFrom(preferredSlots);

  // Format a YYYY-MM-DD (or ISO) string as dd/MM/yyyy.
  const formatYMDLabel = (ymd) => {
    if (!ymd) return null;
    const parts = String(ymd).split("-");
    const d =
      parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date(ymd);
    return isNaN(d.getTime()) ? null : tzLocale(d, "en-GB", undefined);
  };

  // Format a YYYY-MM-DD (or ISO) string in long form, e.g. "19 August 2026".
  // Used in the closure banner so the operator sees the human-readable date
  // rather than a numeric dd/MM/yyyy.
  const formatYMDLabelLong = (ymd) => {
    if (!ymd) return null;
    const parts = String(ymd).split("-");
    const d =
      parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date(ymd);
    return isNaN(d.getTime())
      ? null
      : tzLocale(d, "en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
  };

  // Human-readable name of the patient's preferred period.
  const preferredPeriodName = (() => {
    const slotKey = getPreferredPeriodKey();
    return slotKey === "MORNING"
      ? "Morning"
      : slotKey === "AFTERNOON"
        ? "Afternoon"
        : slotKey === "EVENING"
          ? "Evening"
          : "preferred";
  })();

  const hoursTextFrom = (oh) =>
    oh && oh.start && oh.end
      ? `${minutesToLabel12(hhmmToMinutes(oh.start))} - ${minutesToLabel12(hhmmToMinutes(oh.end))}`
      : null;

  // Single source of truth for the preferred-slot availability (drives both the
  // Preferred Time Slot badge and the out-of-hours banner). Returns whether the
  // center is open for the patient's preferred period on the preferred date,
  // plus the reason it's closed:
  //  - CLOSED_DAY  : the center is closed on the preferred date.
  //  - OUT_OF_HOURS: the preferred period is outside that day's operating hours.
  //  - PAST        : the preferred date is today and the operating hours ended.
  const evaluatePreferredSlotStatus = () => {
    if (!preferredSlots) return { loaded: false, open: false, reason: null };
    if (preferredSlots.is_open !== true)
      return { loaded: true, open: false, reason: "CLOSED_DAY" };
    const win = getPreferredSlotWindow();
    if (!win) return { loaded: true, open: false, reason: "OUT_OF_HOURS" };
    const todayStr = nowInDisplayZone().date; // Baghdad date
    if (preferredSlots.date === todayStr) {
      const closeMin = hhmmToMinutes(preferredSlots?.operating_hours?.end);
      const nowMinutes = nowInDisplayZone().minutes; // Baghdad time
      if (!isNaN(closeMin) && nowMinutes >= closeMin)
        return { loaded: true, open: false, reason: "PAST" };
    }
    return { loaded: true, open: true, reason: null };
  };

  // Human-readable banner content (title + detail) explaining why the preferred
  // slot is closed, referencing the patient's preferred slot, the date, and the
  // center's operating hours so the officer understands it's out of time.
  const getPreferredSlotClosedMessage = (reason) => {
    const dateLabel = formatYMDLabel(getPreferredDateYMD());
    const onDate = dateLabel ? ` on ${dateLabel}` : "";
    const hoursText = hoursTextFrom(preferredSlots?.operating_hours);
    if (reason === "CLOSED_DAY")
      return {
        title: "Center closed on the preferred date",
        detail: `The center is closed${onDate}, so the patient's preferred ${preferredPeriodName} slot can't be scheduled that day.`,
      };
    if (reason === "PAST")
      return {
        title: "Preferred time has already passed",
        detail: `The ${preferredPeriodName} slot${onDate} has already passed for today${hoursText ? `. The center's operating hours are ${hoursText}` : ""}.`,
      };
    // OUT_OF_HOURS
    return {
      title: "Preferred time is outside operating hours",
      detail: `The patient's preferred ${preferredPeriodName} slot${onDate} falls outside the center's operating hours${hoursText ? ` (${hoursText})` : ""}.`,
    };
  };

  // Build the availability banner content. Prioritizes the SELECTED SERVICE DATE
  // (the actionable choice) once its slots have loaded — warning when that date
  // is closed or the preferred period falls outside its operating hours — and
  // otherwise falls back to the patient's PREFERRED slot status. Returns
  // `{ key, title, detail }` or null when nothing needs flagging. `key` is a
  // stable signature used to reset the dismiss state when the reason changes.
  const buildSlotBanner = () => {
    const selStr = formatDateYMD(serviceDate);
    if (serviceDate && centerSlots && centerSlots.date === selStr) {
      const dateLabel = formatYMDLabel(selStr);
      const dateLabelLong = formatYMDLabelLong(selStr);
      if (centerSlots.is_open === false) {
        // When the selected service date is still the patient's preferred date
        // (either because we just auto-bound it on page load or the officer
        // hasn't picked a different date yet), reference it as the "preferred
        // date" in the copy so the officer immediately understands which date
        // needs re-picking. If they've since chosen a different closed date,
        // fall back to the neutral wording.
        const preferredYMD = getPreferredDateYMD();
        const isPreferred = preferredYMD && preferredYMD === selStr;
        const detail = isPreferred
          ? `The center is closed on preferred date ${dateLabelLong}. Please choose another service date.`
          : `The center is closed on ${dateLabelLong}. Please choose another service date.`;
        return {
          key: `SVC|CLOSED|${selStr}`,
          title: "Center closed on the selected service date",
          detail,
        };
      }
      if (!getSlotWindowFrom(centerSlots)) {
        const hoursText = hoursTextFrom(centerSlots.operating_hours);
        return {
          key: `SVC|OOH|${selStr}`,
          title: "Selected service date is outside operating hours",
          detail: `The ${preferredPeriodName} slot on ${dateLabel} falls outside the center's operating hours${hoursText ? ` (${hoursText})` : ""}. Please choose another date or time.`,
        };
      }
      return null; // service date is valid → no banner
    }

    // No service date chosen yet → reflect the patient's preferred slot.
    const pref = evaluatePreferredSlotStatus();
    if (pref.loaded && !pref.open) {
      const msg = getPreferredSlotClosedMessage(pref.reason);
      return {
        key: `PREF|${pref.reason}|${getPreferredDateYMD()}`,
        title: msg.title,
        detail: msg.detail,
      };
    }
    return null;
  };

  // Turn a "HH:MM" slot into a { value, label } option with a 12-hour label.
  const slotToOption = (hhmm) => {
    const [h, m] = String(hhmm).split(":").map(Number);
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h >= 12 ? "PM" : "AM";
    return {
      value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      label: `${displayHour}:${String(m).padStart(2, "0")} ${period}`,
    };
  };

  // Low-level call to the center-slots endpoint for a given date (center = auth user).
  const requestCenterSlots = async (dateStr) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = `/api/v1/lab/center-slots${dateStr ? `?date=${dateStr}` : ""}`;
      const res = await axiosInstance.get(url);
      return res?.data?.data || null;
    } catch (e) {
      return null;
    }
  };

  // Service-date-based fetch (Center Availability + Service Time). When
  // notifyIfClosed is true (officer picked the date), a closed day shows a toast.
  const fetchCenterSlots = async (dateStr, notifyIfClosed = false) => {
    const data = await requestCenterSlots(dateStr);
    if (data) {
      setCenterSlots(data);
      if (notifyIfClosed && data.is_open === false) {
        toast.error(
          "The center is closed on the selected date. Please choose another date.",
        );
      }
    }
  };

  // On load and whenever the service date changes, refresh the service-date slots.
  useEffect(() => {
    const dateStr = formatDateYMD(serviceDate) || getPreferredDateYMD();
    if (dateStr) {
      const notify = dateSelectedByUserRef.current;
      dateSelectedByUserRef.current = false;
      fetchCenterSlots(dateStr, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDate, orderDetails?.preferredDate]);

  // Fetch the preferred-date slots ONCE (pinned to the patient's preferred date)
  // so the Preferred Time Slot dot/range never changes with the service date.
  useEffect(() => {
    const pd = getPreferredDateYMD();
    if (pd)
      requestCenterSlots(pd).then((data) => {
        if (data) setPreferredSlots(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.preferredDate]);

  // Get time range based on preferred time slot
  const getTimeRangeForSlot = (timeSlot) => {
    // Prefer the live operating-hours window for the preferred period.
    const win = getPreferredSlotWindow();
    if (win)
      return {
        min: minutesToHHMM(win.startMin),
        max: minutesToHHMM(win.endMin),
      };
    if (!timeSlot) return { min: "00:00", max: "23:59" };

    const slot = timeSlot.toUpperCase();

    if (slot.includes("MORNING")) {
      return { min: "08:00", max: "12:00" };
    } else if (slot.includes("AFTERNOON")) {
      return { min: "12:00", max: "17:00" };
    } else if (slot.includes("EVENING")) {
      return { min: "17:00", max: "19:30" };
    }

    return { min: "00:00", max: "23:59" };
  };

  // Generate time options based on time slot
  const generateTimeOptions = (timeSlot) => {
    const timeRange = getTimeRangeForSlot(timeSlot);
    const options = [];

    // Parse min and max times
    const [minHour, minMinute] = timeRange.min.split(":").map(Number);
    const [maxHour, maxMinute] = timeRange.max.split(":").map(Number);

    // Generate time options in 30-minute intervals
    for (let hour = minHour; hour <= maxHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Skip if before min time
        if (hour === minHour && minute < minMinute) continue;

        // Skip if after max time
        if (hour === maxHour && minute > maxMinute) break;

        // Skip if hour is past max hour
        if (hour > maxHour) break;

        const timeValue = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? "PM" : "AM";
        const displayTime = `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;

        options.push({ value: timeValue, label: displayTime });
      }
    }

    return options;
  };

  // Service Time options come from the center-slots endpoint for the selected
  // service date (the patient's preferred period bucket — already clipped to
  // that day's operating hours). Falls back to the static generator until the
  // slots load; a closed day / preferred period with no slots yields no options.
  const buildTimeOptions = () => {
    const periodKey = getPreferredPeriodKey();
    const bucket = periodKey
      ? centerSlots?.slots?.[periodKey.toLowerCase()]
      : null;
    if (centerSlots) {
      let slots = Array.isArray(bucket?.slots) ? bucket.slots : [];
      // If the selected date is today, drop slots whose start time has already
      // passed (e.g. the morning slots are over by the afternoon → empty list).
      const todayStr = nowInDisplayZone().date; // Baghdad date
      if (centerSlots.date && centerSlots.date === todayStr) {
        const nowMinutes = nowInDisplayZone().minutes; // Baghdad time
        slots = slots.filter((hhmm) => hhmmToMinutes(hhmm) > nowMinutes);
      }
      return slots.map(slotToOption);
    }
    // Not loaded yet — fall back to the previous static generation.
    return generateTimeOptions(orderDetails?.preferredTimeSlot);
  };

  // Get current time range based on order's preferred time slot
  const timeRange = getTimeRangeForSlot(orderDetails?.preferredTimeSlot);
  const timeOptions = buildTimeOptions();

  // Manual test function
  const testFetchOrderDetails = () => {
    console.log("Manual test button clicked");
    console.log("Will fetch order with ID:", orderId);
    if (orderId) {
      fetchOrderDetails();
    } else {
      console.error("No orderId available!");
      toast.error("No order ID found in URL");
    }
  };

  // Manual invoice calculation test
  const testInvoiceCalculation = () => {
    console.log("=== MANUAL INVOICE CALCULATION TEST ===");
    const result = calculateInvoiceTotal();
    console.log("Manual calculation result:", result);
    toast.info(`Total: IQD ${formatWholeAmount(result.total)}`);
  };

  const dispatch = useDispatch();
  const {
    data: apiAppointments,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.fetchDoctorAppointments);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const toggleChat = () => {
    setIsChatOpen((prev) => !prev);
  };

  // Use Redux state for totalPages, fallback to local state if needed
  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  // Static placeholder appointments (kept for reference)

  // Bind appointments from API (normalize shape for the existing UI)
  const appointments = (apiAppointments || []).map((a) => {
    const patient = a?.patient || {};
    const name = `${patient?.full_name || ""} ${
      patient?.surname || ""
    }`.trim();
    const avatar = patient?.avatar_presigned_url || patient?.avatar || user;
    // Determine typeIcon based on consultation_type
    const typeIcon =
      a.consultation_type === "video"
        ? "video"
        : a.consultation_type === "audio"
          ? "mic"
          : "message";
    const type = a.consultation_type === "video" ? "Online Consult" : "On-Site";
    return {
      id: a.id,
      name,
      avatar,
      location: a?.location || a?.onsite_details || "",
      date: a?.appointment_date || a?.booking_date || "",
      time:
        a?.formatted_time ||
        (a?.appointment_time_slot
          ? a.appointment_time_slot.charAt(0).toUpperCase() +
            a.appointment_time_slot.slice(1)
          : ""),
      typeIcon,
      type,
      status: a?.status || "",
      raw: a,
    };
  });

  // Sort appointments (filtering is done server-side)
  const sortedAppointments = useMemo(() => {
    // Sort appointments
    const sorted = [...appointments].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  // Pagination
  // const totalPages = Math.ceil(
  //   filteredAndSortedAppointments.length / itemsPerPage
  // );
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedAppointments = filteredAndSortedAppointments.slice(
  //   startIndex,
  //   startIndex + itemsPerPage
  // );

  const paginatedAppointments = sortedAppointments;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Test management functions
  const handleTestInputChange = (field, value) => {
    console.log(
      `handleTestInputChange - Field: ${field}, Value: ${value}, Type: ${typeof value}`,
    );
    setCurrentTest((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      console.log("Updated currentTest:", updated);
      return updated;
    });
  };

  // Fetch lab services for dropdown
  const fetchLabServices = async (page = 1, limit = 100) => {
    setLoadingLabServices(true);
    try {
      const response = await getLabServices({ page, limit });

      console.log("Lab services API response:", response);

      if (response?.status) {
        // Response structure: response.data.services
        const servicesData = response.data?.services || [];

        console.log("Lab services loaded:", servicesData);
        console.log("First service structure:", servicesData[0]);

        setLabServices(servicesData);
        // Silently render the empty state in the dropdown when there are no
        // services yet — don't surface a toast on page load.
      } else {
        setLabServices([]);
        // A "no services available" response is an empty-state, not an error —
        // don't disrupt the appointment-detail view with a toaster. The Add
        // Test dropdown will render its own empty state.
        console.warn(
          "Lab services fetch returned no services:",
          response?.message,
        );
      }
    } catch (error) {
      console.error("Error fetching lab services:", error);
      console.error("Error response:", error.response);
      setLabServices([]); // Set empty array on error
      // Suppress the toast here too — the appointment detail should not throw
      // a global toast on mount just because the services endpoint failed.
    } finally {
      setLoadingLabServices(false);
    }
  };

  // The whole order shares a single service location. Seed it from tests that
  // are already added; otherwise fall back to the patient's preferred location.
  const getEffectiveLocation = () => {
    const existingWithLocation = tests.find((test) => test?.location);
    let loc = existingWithLocation
      ? normalizeLocationCode(existingWithLocation.location)
      : normalizeLocationCode(orderDetails?.preferredLocation);
    // Clamp to what the provider's consultation type permits.
    const allowed = allowedLocationsFromConsultation(consultationType);
    if (!allowed.includes(loc)) loc = allowed[0];
    return loc;
  };

  // Changing the location on any single test applies it to every test in the
  // order, keeping the add-row and edit-row selections in sync as well.
  const applyLocationToAllTests = (newLocation) => {
    const location = normalizeLocationCode(newLocation);
    const transportationFee =
      location === LOCATION_CODES.HOME_VISIT
        ? HOME_VISIT_DEFAULT_TRANSPORTATION_FEE
        : "0";

    setTests((prev) =>
      prev.map((test) => ({ ...test, location, transportationFee })),
    );
    setCurrentTest((prev) => ({ ...prev, location, transportationFee }));
    setEditingTest((prev) => ({ ...prev, location, transportationFee }));
  };

  // Handle test selection from dropdown
  const handleTestSelection = (serviceId) => {
    console.log("=== HANDLE TEST SELECTION ===");
    console.log("serviceId:", serviceId);
    console.log("labServices:", labServices);

    // Empty serviceId means the user cleared the dropdown selection.
    // Reset the test fields so price/location/delivery don't keep stale values.
    if (!serviceId) {
      setSelectedTestId("");
      setCurrentTest({
        testName: "",
        serviceCode: "",
        price: "",
        location: "ON_SITE",
        transportationFee: "0",
      });
      return;
    }

    if (!Array.isArray(labServices)) {
      console.log("Early return - labServices not array");
      return;
    }

    setSelectedTestId(serviceId); // Set the selected value

    const selectedService = labServices.find(
      (service) => service.id === parseInt(serviceId),
    );
    console.log("selectedService:", selectedService);

    if (selectedService) {
      // Location follows the order's effective location (preferred location, or
      // whatever the already-added tests are set to) rather than the value the
      // service was created with. Transportation fee is derived from that location.
      const location = getEffectiveLocation();
      const transportationFee =
        location === LOCATION_CODES.HOME_VISIT
          ? HOME_VISIT_DEFAULT_TRANSPORTATION_FEE
          : "0";

      const newTestData = {
        testName: selectedService.testName || "",
        serviceCode: selectedService.serviceCode || "",
        price: selectedService.price || "",
        location,
        transportationFee,
      };

      console.log("Setting currentTest to:", newTestData);

      setCurrentTest((prev) => ({
        ...prev,
        ...newTestData,
      }));

      console.log("Selected service:", selectedService);
      console.log("Calculated transportation fee:", transportationFee);
    } else {
      console.log("No selectedService found");
    }
  };

  const addTestToList = () => {
    console.log("=== ADD TEST VALIDATION ===");
    console.log("currentTest:", currentTest);
    console.log(
      "testName:",
      currentTest.testName,
      "Type:",
      typeof currentTest.testName,
    );
    console.log(
      "serviceCode:",
      currentTest.serviceCode,
      "Type:",
      typeof currentTest.serviceCode,
    );
    console.log("price:", currentTest.price, "Type:", typeof currentTest.price);
    console.log("location:", currentTest.location);

    // Validate test data - check for empty strings and whitespace
    const testName = currentTest.testName?.toString()?.trim();
    const serviceCode = currentTest.serviceCode?.toString()?.trim();
    const price = currentTest.price?.toString()?.trim();

    if (!testName || !serviceCode || !price) {
      console.log("Validation failed:");
      console.log(
        "- testName empty:",
        !testName,
        "Value:",
        `"${currentTest.testName}"`,
      );
      console.log(
        "- serviceCode empty:",
        !serviceCode,
        "Value:",
        `"${currentTest.serviceCode}"`,
      );
      console.log("- price empty:", !price, "Value:", `"${currentTest.price}"`);
      toast.error(
        "Please fill in all required fields (Test Name, Service Code, Price)",
      );
      return;
    }

    // Check for duplicate tests
    const isDuplicate = tests.some(
      (test) =>
        test.testName === currentTest.testName &&
        test.serviceCode === currentTest.serviceCode,
    );

    if (isDuplicate) {
      toast.error("This test is already added to the list");
      return;
    }

    console.log("Adding test to list:", currentTest);
    console.log("Preferred location:", orderDetails?.preferredLocation);

    // Add test to list
    setTests((prev) => [...prev, { ...currentTest, id: Date.now() }]);

    // Reset current test form and dropdown
    setCurrentTest({
      testName: "",
      serviceCode: "",
      price: "",
      location: "ON_SITE",
      transportationFee: "0",
    });
    setSelectedTestId(""); // Reset dropdown selection
    setShowAddTestRow(false); // Hide the add test row

    toast.success("Test added to list");
  };

  const removeTestFromList = (testId) => {
    setTests((prev) => prev.filter((test) => test.id !== testId));
    toast.info("Test removed from list");
  };

  // Tests already present in the Test Details list should not appear again in the
  // "Add Test" dropdown. (tests state is seeded from the saved order tests.)
  const addedTestCodes = new Set(
    [...(tests || []), ...(orderDetails?.tests || [])]
      .map((t) => (t.serviceCode || t.service_code || "").toString())
      .filter(Boolean),
  );
  const availableLabServices = (
    Array.isArray(labServices) ? labServices : []
  ).filter(
    (s) =>
      !addedTestCodes.has((s.serviceCode || s.service_code || "").toString()),
  );
  // For the edit row, keep the test currently being edited selectable.
  const availableLabServicesForEdit = (
    Array.isArray(labServices) ? labServices : []
  ).filter((s) => {
    const code = (s.serviceCode || s.service_code || "").toString();
    return (
      !addedTestCodes.has(code) ||
      code === (editingTest?.serviceCode || "").toString()
    );
  });

  // Edit test functions
  const startEditingTest = (test) => {
    setEditingTestId(test.id);
    setEditingTest({
      testName: test.testName,
      serviceCode: test.serviceCode,
      price: test.price,
      location: test.location,
      transportationFee: test.transportationFee || "0",
    });

    // Find the service ID from labServices based on test name
    const matchingService = labServices.find(
      (service) => service.testName === test.testName,
    );
    setEditingTestServiceId(
      matchingService ? matchingService.id.toString() : "",
    );
  };

  const handleEditTestInputChange = (field, value) => {
    setEditingTest((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditTestSelection = (serviceId) => {
    // Empty serviceId means the user cleared the dropdown selection.
    if (!serviceId) {
      setEditingTestServiceId("");
      setEditingTest({
        testName: "",
        serviceCode: "",
        price: "",
        location: "ON_SITE",
        transportationFee: "0",
      });
      return;
    }

    if (!Array.isArray(labServices)) return;

    setEditingTestServiceId(serviceId);

    const selectedService = labServices.find(
      (service) => service.id === parseInt(serviceId),
    );
    if (selectedService) {
      setEditingTest((prev) => ({
        ...prev,
        testName: selectedService.testName || "",
        serviceCode: selectedService.serviceCode || "",
        price: selectedService.price || "",
        location: selectedService.location || "ON_SITE",
        transportationFee: "0",
      }));
    }
  };

  const saveEditedTest = () => {
    if (
      !editingTest.testName ||
      !editingTest.serviceCode ||
      !editingTest.price
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setTests((prev) =>
      prev.map((test) =>
        test.id === editingTestId ? { ...test, ...editingTest } : test,
      ),
    );

    // Reset editing state
    setEditingTestId(null);
    setEditingTest({
      testName: "",
      serviceCode: "",
      price: "",
      location: "ON_SITE",
      transportationFee: "0",
    });
    setEditingTestServiceId("");

    toast.success("Test updated successfully");
  };

  const cancelEditingTest = () => {
    setEditingTestId(null);
    setEditingTest({
      testName: "",
      serviceCode: "",
      price: "",
      location: "ON_SITE",
      transportationFee: "0",
    });
    setEditingTestServiceId("");
  };

  // Determine which button to show
  const getActionButton = () => {
    // First-received order: the center must Accept or Reject before it can
    // add tests / send the invoice. Accept keeps the status as REQUEST_SUBMITTED
    // (backend flips lab_accepted); Reject → REJECTED. The Accept / Reject
    // buttons live in the "Acceptance required" panel at the top of the
    // Order Details tab, so nothing is rendered down here for that step.
    const isLabAccepted = orderDetails?.labAccepted === true;
    const isAwaitingCenterDecision =
      orderDetails?.status === "REQUEST_SUBMITTED" &&
      !isLabAccepted &&
      !orderDetails?.patientConfirmationReceivedAt;
    if (isAwaitingCenterDecision) {
      return null;
    }

    // A rejected order is explained by the red "Order rejected" panel (with the
    // reason) at the top of the Order Details tab, so nothing renders down here.
    if (orderDetails?.status === "REJECTED") {
      return null;
    }

    // Handle ORDER_ACCEPTED status - show Assign Technician / Start Processing button
    if (orderDetails?.status === "ORDER_ACCEPTED") {
      const isVisitCenter = isOnSiteOrder;
      return (
        <button
          className={styles["apt-register-btn"]}
          onClick={() => setActiveTab("Technician Detail")}
          style={{ backgroundColor: "#077272", color: "white" }}
        >
          {isVisitCenter ? (
            <>
              <FaLocationArrow />{" "}
              {t("lab.appointmentDetail.startProcessing", "Start Processing")}
            </>
          ) : (
            <>
              <FaLocationArrow />{" "}
              {t("lab.appointmentDetail.assignTechnician", "Assign Technician")}
            </>
          )}
        </button>
      );
    }

    const displayTests = tests.length > 0 ? tests : orderDetails?.tests || [];
    const hasHV = displayTests.some((t) => t.location === "HOME_VISIT");
    const needsTransportationFee = hasHV && !transportationFeeAdded;
    const noTests = displayTests.length === 0;
    const invoiceAlreadySent =
      orderDetails?.status &&
      !["REQUEST_SUBMITTED"].includes(orderDetails.status);
    const awaitingSplit = orderDetails?.status === "AWAITING_CONFIRMATION";

    // Contextual label shown on the (disabled) action button once the invoice has been sent,
    // based on where the order currently is in the workflow.
    // (INVOICE_RECEIVED / AWAITING_PAYMENT have no message here: the blue
    // "Invoice sent" panel at the top of the Order Details tab covers them.)
    const statusActionLabel = (() => {
      switch (orderDetails?.status) {
        case "ORDER_ACCEPTED":
          return t(
            "lab.appointmentDetail.statusPaymentReceivedAssignTechnician",
            "Payment received. Please assign a technician to proceed with the order.",
          );
        case "SCHEDULED":
        case "TECHNICIAN_ASSIGNED":
          return t(
            "lab.appointmentDetail.statusTechnicianAssignedScheduled",
            "A technician has been assigned and the visit is scheduled for the patient.",
          );
        case "TECHNICIAN_DISPATCHED":
          return t(
            "lab.appointmentDetail.statusTechnicianDispatched",
            "The technician has been dispatched and is on the way to the patient.",
          );
        case "IN_PROGRESS":
          return t(
            "lab.appointmentDetail.statusTechnicianCollecting",
            "The technician is collecting the samples from the patient.",
          );
        case "COMPLETED": {
          const hasUploadedResults =
            (orderDetails?.documents || []).some(
              (doc) => doc.document_type === "TEST_RESULT",
            ) || uploadedFiles.length > 0;
          return hasUploadedResults
            ? t(
                "lab.appointmentDetail.statusCompletedResultsGenerated",
                "The order has been completed and the results have been generated.",
              )
            : t(
                "lab.appointmentDetail.statusCompletedUploadResults",
                "The order has been completed. Please upload the results in the Results & Reports section to share them with the patient.",
              );
        }
        case "REJECTED": {
          // Append the reason the officer gave when rejecting, when there is one.
          const rejectedBase = t(
            "lab.appointmentDetail.statusRejected",
            "This order has been rejected.",
          );
          const reason = orderDetails?.rejectionReason;
          return reason ? `${rejectedBase} ${reason}` : rejectedBase;
        }
        default:
          return t(
            "lab.appointmentDetail.statusInvoiceSent",
            "The invoice has been sent to the patient.",
          );
      }
    })();

    // Green for finished/positive states, red for rejected, yellow for in-progress/pending.
    const statusActionColor = (() => {
      switch (orderDetails?.status) {
        case "ORDER_ACCEPTED":
        case "COMPLETED":
          return "#16a34a"; // green
        case "REJECTED":
          return "#dc2626"; // red
        default:
          return "#D08700"; // yellow / amber
      }
    })();

    const isDisabled =
      isSavingTests || noTests || needsTransportationFee || invoiceAlreadySent;

    // Once the invoice has been sent, show a coloured status message instead of a button.
    if (invoiceAlreadySent) {
      // Invoice sent, waiting for payment: explained by the blue "Invoice sent"
      // panel at the top of the Order Details tab, so nothing renders down here.
      if (isAwaitingPatientPayment) {
        return null;
      }
      // Soft background / border tones to match the status accent colour.
      const statusBoxStyles = (() => {
        switch (statusActionColor) {
          case "#16a34a": // green
            return {
              backgroundColor: "#e8f5e8",
              border: "1px solid #4caf50",
              color: "#2e7d32",
            };
          case "#dc2626": // red
            return {
              backgroundColor: "#fdecea",
              border: "1px solid #ef5350",
              color: "#c62828",
            };
          default: // amber
            return {
              backgroundColor: "#fff8e1",
              border: "1px solid #f0b429",
              color: "#D08700",
            };
        }
      })();

      return (
        <div
          style={{
            width: "100%",
            ...statusBoxStyles,
            borderRadius: "8px",
            padding: "12px 16px",
            fontSize: "14px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Check size={16} style={{ marginRight: "8px", flexShrink: 0 }} />
          {awaitingSplit
            ? t(
                "lab.appointmentDetail.awaitingPatientConfirmation",
                "Awaiting Patient Confirmation",
              )
            : statusActionLabel}
        </div>
      );
    }

    return (
      <>
        {needsTransportationFee && !noTests && !invoiceAlreadySent && (
          <div
            style={{
              width: "100%",
              textAlign: "center",
              marginBottom: "8px",
              fontSize: "13px",
              color: "#F59E0B",
            }}
          >
            {t(
              "lab.appointmentDetail.addTransportationFeeWarning",
              "Please add a transportation fee for home visit tests before sending the invoice.",
            )}
          </div>
        )}
        <button
          className={styles["apt-register-btn"]}
          onClick={handleGenerateInvoice}
          disabled={isDisabled}
          style={{
            opacity: isDisabled ? 0.5 : 1,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        >
          <FaLocationArrow />{" "}
          {isSavingTests
            ? t("lab.appointmentDetail.sending", "Sending...")
            : t(
                "lab.appointmentDetail.sendInvoiceToPatient",
                "Send Invoice to Patient",
              )}
        </button>
      </>
    );
  };

  // Fetch lab service fees (admin fee and consultation fee) from new endpoint
  const fetchLabServiceFees = async () => {
    try {
      console.log(
        "FEES_DEBUG - Fetching lab service fees using service function",
      );

      const result = await getLabServiceFeesConfiguration();
      console.log("FEES_DEBUG - Lab service fees response:", result);

      if (result.status && result.data) {
        // Set admin fee percentage
        if (
          result.data.admin_fee &&
          result.data.admin_fee.is_active &&
          result.data.admin_fee.percentage !== undefined
        ) {
          const adminPercentage = parseFloat(result.data.admin_fee.percentage);
          console.log(
            "FEES_DEBUG - Setting admin fee percentage to:",
            adminPercentage,
          );
          setAdminFeePercentage(adminPercentage);
        } else {
          console.log(
            "FEES_DEBUG - Admin fee is inactive or not configured, applying 0% (no admin fee)",
          );
          // No admin fee is configured/active in the admin panel → charge nothing.
          setAdminFeePercentage(0);
        }

        // Set consultation fee percentage (commission)
        if (
          result.data.consultation_fee &&
          result.data.consultation_fee.is_active &&
          result.data.consultation_fee.percentage !== undefined
        ) {
          const consultationPercentage = parseFloat(
            result.data.consultation_fee.percentage,
          );
          console.log(
            "FEES_DEBUG - Setting commission percentage to:",
            consultationPercentage,
          );
          setConsultationFeePercentage(consultationPercentage);
        } else {
          console.log(
            "FEES_DEBUG - Commission is inactive or not configured, using default 0%",
          );
          setConsultationFeePercentage(0);
        }

        return {
          // The percentage comes from the admin panel configuration. `|| 0` (not a
          // hardcoded rate) because a missing/zero percentage means NO admin fee is
          // configured — inventing one here would overcharge the patient.
          adminFee: result.data.admin_fee.percentage || 0,
          consultationFee: result.data.consultation_fee.percentage || 0,
        };
      } else {
        console.log(
          "FEES_DEBUG - Invalid response structure; applying 0% (no admin fee)",
        );
        // Never fall back to a hardcoded rate: the admin fee is configured in the admin
        // panel and nowhere else. If we cannot read it, charge nothing rather than invent
        // a percentage that the platform never configured. Matches the "inactive or not
        // configured" branch above and the `useState(0)` default.
        setAdminFeePercentage(0);
        setConsultationFeePercentage(0);
        return { adminFee: 0, consultationFee: 0 };
      }
    } catch (error) {
      console.error("FEES_DEBUG - Error fetching lab service fees:", error);
      setAdminFeePercentage(0);
      setConsultationFeePercentage(0);
      return { adminFee: 0, consultationFee: 0 };
    }
  };

  // Fetch order details
  const fetchOrderDetails = async () => {
    console.log("=== fetchOrderDetails START ===");
    console.log("orderId:", orderId);
    console.log("typeof orderId:", typeof orderId);

    if (!orderId) {
      console.warn("No order ID provided");
      toast.warning("No order ID found in URL");
      return;
    }

    setLoadingOrderDetails(true);
    console.log("Calling getLabOrderDetails API for orderId:", orderId);
    console.log(
      "Full API URL will be:",
      `${process.env.REACT_APP_API_URL}/api/v1/lab/orders/${orderId}`,
    );

    try {
      const response = await getLabOrderDetails(orderId);
      console.log("Order details response:", response);
      console.log("Response status:", response?.status);
      console.log("Response data:", response?.data);

      if (response && response.status) {
        setOrderDetails(response.data);
        console.log("Order details set successfully:", response.data);
        console.log(
          "ADMIN_FEE_FETCH_DEBUG - Admin fee percentage from API:",
          response.data?.admin_fee_percentage,
        );

        // Sync transportation fee from API
        const apiTransportationFee =
          response.data?.invoice?.transportation_fee ??
          response.data?.transportation_fee ??
          0;
        const feeStr = String(apiTransportationFee);
        setOrderTransportationFee(feeStr);
        // An explicit 0 IQD fee stays "added".
        const syncedFee = parseFloat(feeStr) || 0;
        setTransportationFeeAdded(
          syncedFee > 0 || (syncedFee === 0 && explicitZeroFeeRef.current),
        );

        // If admin fee percentage is not in order details, fetch it separately
        if (
          !response.data?.admin_fee_percentage &&
          !response.data?.invoice?.admin_fee_percentage
        ) {
          console.log(
            "FEES_DEBUG - Admin fee percentage not found in order details, fetching lab service fees",
          );
          await fetchLabServiceFees();
        } else {
          // Use the admin fee percentage from order details
          const orderAdminFee =
            response.data?.admin_fee_percentage ||
            response.data?.invoice?.admin_fee_percentage;
          console.log(
            "FEES_DEBUG - Using admin fee percentage from order details:",
            orderAdminFee,
          );
          setAdminFeePercentage(orderAdminFee);
          // Still fetch consultation fee since it's not in order details
          const fees = await fetchLabServiceFees();
          setAdminFeePercentage(orderAdminFee); // Keep the order details admin fee
        }

        // Set gender based on preferred technician gender
        if (response.data.preferredTechnicianGender) {
          const preferredGender =
            response.data.preferredTechnicianGender.toLowerCase();
          if (preferredGender === "male" || preferredGender === "female") {
            setGender(preferredGender);
            console.log("Gender set to:", preferredGender);
          }
        }

        // If tests exist in order details, populate the tests list
        if (response.data.tests && response.data.tests.length > 0) {
          const formattedTests = response.data.tests.map((test) => {
            const formattedTest = {
              id: test.id || Date.now() + Math.random(),
              testName: test.test_name || test.testName,
              serviceCode: test.service_code || test.serviceCode,
              price: test.price,
              location: test.location,
              transportationFee: test.transportation_fee || test.transportationFee || "0",
            };

            return formattedTest;
          });

          setTests(formattedTests);
          console.log("Tests populated:", formattedTests);
        }

        // Populate service date and time if available. If the order hasn't
        // been scheduled yet, default the service date to the patient's
        // preferred date so the officer starts from a sensible baseline (and
        // the closure banner immediately reflects whether that preferred day
        // is bookable). The officer can still change it from the date picker.
        // Dates are parsed as local calendar days so the input shows the right day.
        // The default never falls before the earliest allowed service date.
        if (parseYMDLocal(response.data.scheduledDate)) {
          setServiceDate(parseYMDLocal(response.data.scheduledDate));
        } else if (response.data.preferredDate) {
          setServiceDate(earliestServiceDate(response.data.preferredDate));
        }
        if (response.data.scheduledTime) {
          setServiceTime(response.data.scheduledTime);
        }

        // Don't show success toast here to avoid duplicate notifications
      } else {
        console.error("Failed response:", response);
        toast.error(response?.message || "Failed to fetch order details");
      }
    } catch (error) {
      console.error("=== ERROR FETCHING ORDER DETAILS ===");
      console.error("Error object:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      console.error("Error message:", error.message);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch order details";
      toast.error(errorMessage);
    } finally {
      setLoadingOrderDetails(false);
      console.log("=== fetchOrderDetails END ===");
    }
  };

  // Fetch available technicians
  const fetchAvailableTechnicians = async () => {
    if (!orderId) {
      console.warn("No order ID provided for fetching technicians");
      return;
    }

    setLoadingTechnicians(true);
    try {
      const response = await getAvailableTechnicians(orderId);
      console.log("Available technicians response:", response);

      if (response && response.status) {
        setAvailableTechnicians(response.data || []);
        console.log("Available technicians set successfully:", response.data);
      } else {
        console.error("Failed to fetch technicians:", response);
        toast.error(
          response?.message || "Failed to fetch available technicians",
        );
      }
    } catch (error) {
      console.error("Error fetching available technicians:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch available technicians";
      toast.error(errorMessage);
    } finally {
      setLoadingTechnicians(false);
    }
  };

  // Assign technician and send details via WhatsApp
  const handleAssignTechnician = async () => {
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    // Check if technician is already assigned
    if (orderDetails?.assignedTechnicianId) {
      toast.info("A technician is already assigned to this order");
      return;
    }

    // Check if payment is done (status should be ORDER_ACCEPTED or TECHNICIAN_ASSIGNED)
    if (
      orderDetails?.status !== "ORDER_ACCEPTED" &&
      orderDetails?.status !== "TECHNICIAN_ASSIGNED"
    ) {
      toast.error(
        "Payment is not completed yet. You can only assign a technician after payment is done.",
      );
      return;
    }

    if (!selectedTechnicianId) {
      toast.error(
        t(
          "lab.appointmentDetail.selectTechnicianBeforeProceeding",
          "Please select a technician before proceeding.",
        ),
      );
      return;
    }

    setAssigningTechnician(true);
    try {
      console.log(
        "Assigning technician:",
        selectedTechnicianId,
        "to order:",
        orderId,
      );

      const response = await assignLabOrderTechnician(
        orderId,
        parseInt(selectedTechnicianId),
      );
      console.log("Assign technician response:", response);

      if (response && response.status) {
        toast.success(response.message || "Technician assigned successfully");

        // Refresh order details to show updated technician
        await fetchOrderDetails();

        // Optionally refresh technicians list to update availability
        await fetchAvailableTechnicians();

        // TODO: Implement WhatsApp notification here
        // The backend should handle sending WhatsApp notification to patient
      } else {
        toast.error(response?.message || "Failed to assign technician");
      }
    } catch (error) {
      console.error("Error assigning technician:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to assign technician";
      toast.error(errorMessage);
    } finally {
      setAssigningTechnician(false);
    }
  };

  // Accept a newly-received order. Keeps status REQUEST_SUBMITTED (backend only
  // flips the lab_accepted flag) and notifies the patient, then the officer
  // can add tests + send the invoice. Confirmed via a dialog.
  const confirmAcceptOrder = async () => {
    setAcceptingOrder(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.post(
        `/api/v1/lab/orders/${orderId}/accept`,
      );
      if (response?.data?.status) {
        toast.success("Order accepted");
        setShowAcceptModal(false);
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to accept order");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to accept order");
    } finally {
      setAcceptingOrder(false);
    }
  };

  // Reject a newly-received order. Sets status REJECTED and notifies the patient
  // (bilingual, persisted) with the optional reason. Confirmed via a dialog.
  const confirmRejectOrder = async () => {
    // A preset reason submits its own text; "Other" submits the free-text box.
    const presetReason = resolveReasonText(selectedRejectReasonId);
    const finalReason = presetReason || rejectReason.trim();

    setRejectingOrder(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.post(
        `/api/v1/lab/orders/${orderId}/reject`,
        {
          rejectionReason: finalReason || undefined,
        },
      );
      if (response?.data?.status) {
        toast.success("Order rejected");
        setShowRejectModal(false);
        resetRejectForm();
        navigate("/lab_ordermanagement");
      } else {
        toast.error(response?.data?.message || "Failed to reject order");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reject order");
    } finally {
      setRejectingOrder(false);
    }
  };

  // Generate invoice
  const handleGenerateInvoice = async () => {
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    // Validate tests are added
    if (tests.length === 0) {
      toast.error("Please add at least one test before generating invoice");
      return;
    }

    // Validate service date and time
    if (!serviceDate) {
      toast.error("Please select a service date");
      return;
    }

    if (isBeforePreferredDate(serviceDate)) {
      toast.error(
        `Service date cannot be before the patient's preferred date (${format(preferredServiceDate, "dd/MM/yyyy")}).`,
      );
      return;
    }

    if (!serviceTime || serviceTime.trim() === "") {
      toast.error("Please enter a service time");
      return;
    }

    // Validate service time is within the preferred time slot range
    const timeRange = getTimeRangeForSlot(orderDetails?.preferredTimeSlot);
    if (serviceTime < timeRange.min || serviceTime > timeRange.max) {
      const slotName =
        orderDetails?.preferredTimeSlot || "the selected time slot";
      toast.error(
        `Service time must be between ${timeRange.min} and ${timeRange.max} for ${slotName}`,
      );
      return;
    }

    try {
      setIsSavingTests(true);

      // Prepare scheduling data. Use the LOCAL calendar date, not toISOString()
      // — the latter converts the local-midnight Date to UTC and rolls back a
      // day for timezones ahead of UTC (e.g. Baghdad UTC+3 turned 29th → 28th).
      const formattedDate = formatDateYMD(serviceDate); // Format: YYYY-MM-DD
      // The centre works on Baghdad time (fixed), whatever this device's clock says.
      const timezone = PLATFORM_TIMEZONE;

      console.log("Step 1: Adding tests to order...");

      // Step 1: Add tests to the order
      const testsPayload = tests.map((test) => ({
        testName: test.testName,
        serviceCode: test.serviceCode,
        price: parseFloat(test.price),
        location: test.location,
        transportationFee: parseFloat(test.transportationFee || 0),
      }));

      console.log("Tests payload:", testsPayload);

      const addTestsResponse = await addLabOrderTests(
        orderId,
        testsPayload,
        tests.some((t) => t.location === "HOME_VISIT")
          ? parseFloat(orderTransportationFee) || 0
          : 0,
      );

      console.log("Add tests response:", addTestsResponse);

      if (!addTestsResponse || !addTestsResponse.status) {
        const errorMsg = addTestsResponse?.message || "Failed to add tests";
        toast.error(errorMsg);
        console.error("Add tests failed:", addTestsResponse);
        return;
      }

      toast.success("Tests added successfully");

      console.log("Step 2: Scheduling appointment...");
      console.log("Schedule data:", { formattedDate, serviceTime, timezone });

      // Step 2: Schedule the appointment (PUT request)
      const scheduleResponse = await scheduleLabOrderAppointment(
        orderId,
        formattedDate,
        serviceTime,
        timezone,
      );

      console.log("Schedule response:", scheduleResponse);

      if (!scheduleResponse || !scheduleResponse.status) {
        const errorMsg =
          scheduleResponse?.message || "Failed to schedule appointment";
        toast.error(errorMsg);
        console.error("Schedule failed:", scheduleResponse);
        return;
      }

      toast.success("Appointment scheduled successfully");

      console.log("Step 3: Generating invoice...");

      // Step 3: Generate and send the invoice
      const invoiceResponse = await generateLabOrderInvoice(orderId);

      console.log("Invoice response:", invoiceResponse);

      // Check if the response indicates success
      if (invoiceResponse && invoiceResponse.status) {
        toast.success("Invoice generated and sent to patient successfully");

        // Clear tests list after successful invoice generation
        setTests([]);

        // Refresh order details to show updated status
        await fetchOrderDetails();
      } else {
        // Show the specific error message from backend
        const errorMsg =
          invoiceResponse?.message || "Failed to generate invoice";
        const errorCode = invoiceResponse?.error_code || "";
        toast.error(`${errorMsg} ${errorCode ? `(${errorCode})` : ""}`);
        console.error("Invoice generation failed:", invoiceResponse);
      }
    } catch (error) {
      console.error("Error in handleGenerateInvoice:", error);
      console.error("Error response:", error.response?.data);

      // Extract error message from response
      const errorData = error.response?.data;
      let errorMessage = "Failed to process request";

      if (errorData) {
        errorMessage = errorData.message || errorMessage;
        if (errorData.error_code) {
          errorMessage += ` (${errorData.error_code})`;
        }
      } else {
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
    } finally {
      setIsSavingTests(false);
    }
  };

  // Share results with patient
  const handleShareWithPatient = async () => {
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info("Report has already been shared with the patient");
      return;
    }

    try {
      console.log("Sharing results with patient for order:", orderId);

      // Create axios instance with base URL and auth token
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Call the resend report endpoint to share results with patient
      const response = await axiosInstance.post(
        `${APP_PATH.resendReport}/${orderId}/resend`,
        {},
      );

      if (response.data.status) {
        toast.success("Results shared with patient successfully");
        // Refresh order details to get updated report status
        fetchOrderDetails();
      } else {
        toast.error(
          response.data.message || "Failed to share results with patient",
        );
      }
    } catch (error) {
      console.error("Error sharing results with patient:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to share results with patient");
      }
    }
  };

  // Handle file upload for test results
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. Upload is disabled.",
      );
      event.target.value = ""; // Clear the file input
      return;
    }

    // Validate file type (only PDF)
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed for test results");
      return;
    }

    // Validate file size (25MB max)
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 25MB");
      return;
    }

    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    setUploadingFile(true);

    try {
      console.log("Uploading test results file for order:", orderId);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", file);

      // Create axios instance with base URL and auth token
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Choose endpoint based on file size
      const fileSizeMB = file.size / (1024 * 1024);
      const isLargeFile = fileSizeMB > 10;
      const endpoint = isLargeFile
        ? `${APP_PATH.uploadResultsMultipart}/${orderId}/upload-results-multipart`
        : `${APP_PATH.uploadResults}/${orderId}/upload-results`;

      console.log(
        `File size: ${fileSizeMB.toFixed(2)}MB, Using ${isLargeFile ? "multipart" : "regular"} upload`,
      );

      if (isLargeFile) {
        toast.info(
          `Uploading large file (${fileSizeMB.toFixed(1)}MB). This may take a few minutes...`,
        );
      }

      // Upload file using the appropriate endpoint
      const response = await axiosInstance.post(endpoint, formData);

      if (response.data.status) {
        toast.success("Test results uploaded successfully");

        // Add uploaded file to the list
        const uploadedFile = {
          id: response.data.data.id,
          fileName: response.data.data.fileName,
          fileUrl: response.data.data.fileUrl,
          uploadedAt: new Date().toISOString(),
        };

        setUploadedFiles((prev) => [...prev, uploadedFile]);

        // Refresh order details to get updated status
        fetchOrderDetails();

        // Clear the file input
        event.target.value = "";
      } else {
        toast.error(response.data.message || "Failed to upload test results");
      }
    } catch (error) {
      console.error("Error uploading test results:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to upload test results");
      }
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file click to open in new tab
  const handleFileClick = async (fileId, fileName) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      // Show loading state
      toast.info("Opening file...");

      console.log("Attempting to open file:", {
        fileId,
        fileName,
        orderId: urlOrderId,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getFilePresignedUrl}/${urlOrderId}/file/${fileId}/presigned-url`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json();
      console.log("Presigned URL response:", result);

      if (result.status && result.data?.presignedUrl) {
        // Open file in new tab
        window.open(result.data.presignedUrl, "_blank");
      } else {
        console.error("Presigned URL error:", result);
        toast.error(
          result.message ||
            "Failed to open file. The file may not be available or you may not have permission to access it.",
        );
      }
    } catch (error) {
      console.error("Error opening file:", error);
      toast.error(
        "Failed to open file. Please try again or contact support if the issue persists.",
      );
    }
  };

  // Handle file deletion
  const handleFileDelete = async (fileId, fileName) => {
    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. File deletion is disabled.",
      );
      return;
    }

    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      );

      if (!confirmed) {
        return;
      }

      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      // Show loading state
      toast.info("Deleting file...");

      console.log("Attempting to delete file:", {
        fileId,
        fileName,
        orderId: urlOrderId,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.deleteTestResultFile}/${urlOrderId}/file/${fileId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json();
      console.log("Delete file response:", result);

      if (result.status) {
        toast.success("File deleted successfully");

        // Refresh order details to update the file list
        await fetchOrderDetails();
      } else {
        console.error("Delete file error:", result);
        toast.error(
          result.message || "Failed to delete file. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(
        "Failed to delete file. Please try again or contact support if the issue persists.",
      );
    }
  };

  // Handle multiple file upload for test results (also handles single files)
  const handleMultipleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. Upload is disabled.",
      );
      event.target.value = ""; // Clear the file input
      return;
    }

    // Prevent duplicate uploads by clearing the input immediately
    const inputElement = event.target;

    // If only one file is selected, use single file upload for better compatibility
    if (files.length === 1) {
      const file = files[0];

      // Clear the input to prevent duplicate uploads
      inputElement.value = "";

      // Validate file type (only PDF)
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files are allowed for test results");
        return;
      }

      // Validate file size (25MB max)
      const maxSize = 25 * 1024 * 1024; // 25MB
      if (file.size > maxSize) {
        toast.error("File size must be less than 25MB");
        return;
      }

      if (!orderId) {
        toast.error("No order ID available");
        return;
      }

      // Prevent multiple uploads if already uploading
      if (uploadingFile) {
        toast.warning("Upload already in progress, please wait...");
        return;
      }

      setUploadingFile(true);

      try {
        console.log("Uploading single test results file for order:", orderId);

        // Create FormData for file upload
        const formData = new FormData();
        formData.append("file", file);

        // Create axios instance with base URL and auth token
        const token = sessionStorage.getItem("usr_1a2b3c");
        const axiosInstance = axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        // Choose endpoint based on file size
        const fileSizeMB = file.size / (1024 * 1024);
        const isLargeFile = fileSizeMB > 10;
        const endpoint = isLargeFile
          ? `${APP_PATH.uploadResultsMultipart}/${orderId}/upload-results-multipart`
          : `${APP_PATH.uploadResults}/${orderId}/upload-results`;

        console.log(
          `File size: ${fileSizeMB.toFixed(2)}MB, Using ${isLargeFile ? "multipart" : "regular"} upload`,
        );

        if (isLargeFile) {
          toast.info(
            `Uploading large file (${fileSizeMB.toFixed(1)}MB). This may take a few minutes...`,
          );
        }

        // Upload file using the appropriate endpoint
        const response = await axiosInstance.post(endpoint, formData);

        if (response.data.status) {
          toast.success("Test results uploaded successfully");

          // Refresh order details to get updated status (file will appear from orderDetails.documents)
          await fetchOrderDetails();
        } else {
          toast.error(response.data.message || "Failed to upload test results");
        }
      } catch (error) {
        console.error("Error uploading test results:", error);
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to upload test results");
        }
      } finally {
        setUploadingFile(false);
      }
      return;
    }

    // Handle multiple files
    // Clear the input to prevent duplicate uploads
    inputElement.value = "";

    // Validate file count (max 10 files)
    if (files.length > 10) {
      toast.error("Maximum 10 files allowed per upload");
      return;
    }

    // Validate each file
    const maxSize = 25 * 1024 * 1024; // 25MB
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type (only PDF)
      if (file.type !== "application/pdf") {
        toast.error(
          `File ${i + 1} (${file.name}): Only PDF files are allowed for test results`,
        );
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        toast.error(
          `File ${i + 1} (${file.name}): File size must be less than 25MB`,
        );
        return;
      }
    }

    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    // Prevent multiple uploads if already uploading
    if (uploadingFile) {
      toast.warning("Upload already in progress, please wait...");
      return;
    }

    setUploadingFile(true);

    try {
      console.log(
        `Uploading ${files.length} test results files for order:`,
        orderId,
      );

      // Create FormData for multiple file upload
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append("files", file);
      });

      // Create axios instance with base URL and auth token
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const endpoint = `${APP_PATH.uploadMultipleResults}/${orderId}/upload-multiple-results`;

      console.log(
        `Uploading ${files.length} files using multi-file upload endpoint`,
      );

      // Calculate total size for progress indication
      const totalSizeMB =
        files.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
      if (totalSizeMB > 50) {
        toast.info(
          `Uploading ${files.length} files (${totalSizeMB.toFixed(1)}MB total). This may take several minutes...`,
        );
      }

      // Upload files using the multi-file endpoint
      const response = await axiosInstance.post(endpoint, formData);

      if (response.data.status) {
        const {
          successCount,
          failedCount,
          totalFiles,
          uploadedFiles,
          failedUploads,
        } = response.data.data;

        if (failedCount === 0) {
          toast.success(`All ${totalFiles} files uploaded successfully`);
        } else if (successCount > 0) {
          toast.warning(
            `${successCount} of ${totalFiles} files uploaded successfully. ${failedCount} files failed.`,
          );
          // Show details of failed uploads
          if (failedUploads && failedUploads.length > 0) {
            failedUploads.forEach((failed) => {
              toast.error(
                `Failed to upload ${failed.fileName}: ${failed.error}`,
              );
            });
          }
        } else {
          toast.error(`All ${totalFiles} file uploads failed`);
        }

        // Refresh order details to get updated status (files will appear from orderDetails.documents)
        await fetchOrderDetails();
      } else {
        toast.error(response.data.message || "Failed to upload test results");
      }
    } catch (error) {
      console.error("Error uploading multiple test results:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to upload test results");
      }
    } finally {
      setUploadingFile(false);
    }
  };

  // Fetch the provider's consultation type once (constrains test locations).
  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const axiosInstance = axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: { Authorization: `Bearer ${token}` },
        });
        const res = await axiosInstance.get("/api/v1/lab/profile");
        const type = res?.data?.data?.consultation_type;
        if (type) setConsultationType(type);
        if (res?.data?.data) setLabCenterProfile(res.data.data);
      } catch (e) {
        // Best-effort — on failure both locations remain allowed.
      }
    })();
  }, []);

  // Fetch order details on component mount or when orderId changes
  useEffect(() => {
    console.log("LabAppointmentDetail mounted, orderId:", orderId);
    if (orderId) {
      console.log("Fetching order details for orderId:", orderId);
      fetchOrderDetails();
    } else {
      console.warn("No orderId available to fetch order details");
    }

    // Fetch lab services for dropdown
    fetchLabServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Reset active tab if current tab is not available for the current status
  useEffect(() => {
    const orderStatus = orderDetails?.status;
    const isOnSite = isOnSiteOrder;

    const onsiteTechnicianTabStatuses = [
      "ORDER_ACCEPTED",
      "TECHNICIAN_ASSIGNED",
      "IN_PROGRESS",
      "COMPLETED",
    ];
    const homeVisitTechnicianTabStatuses = [
      "ORDER_ACCEPTED",
      "TECHNICIAN_ASSIGNED",
      "SCHEDULED",
      "TECHNICIAN_DISPATCHED",
      "IN_PROGRESS",
      "COMPLETED",
    ];
    const showTechnicianTab = isOnSite
      ? onsiteTechnicianTabStatuses.includes(orderStatus)
      : homeVisitTechnicianTabStatuses.includes(orderStatus);

    console.log(
      "Tab reset check - Status:",
      orderStatus,
      "Active Tab:",
      activeTab,
      "Show Technician Tab:",
      showTechnicianTab,
    );

    if (activeTab === "Technician Detail" && !showTechnicianTab) {
      console.log(
        "Switching to Order Details tab - Technician Detail not available for status:",
        orderStatus,
      );
      setActiveTab("Order Details");
    }

    // Results & Reports only available for COMPLETED (home-visit) or COMPLETED on-site
    if (activeTab === "Results & Reports" && orderStatus !== "COMPLETED") {
      console.log(
        "Switching to Order Details tab - Results & Reports only available for COMPLETED status",
      );
      setActiveTab("Order Details");
    }
  }, [orderDetails?.status, orderDetails?.preferredLocation, activeTab]);

  // Fetch technicians when gender changes
  useEffect(() => {
    if (orderId && gender) {
      console.log("Gender changed to:", gender, "- Fetching technicians");
      fetchAvailableTechnicians();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender]);
  const saveTestsToOrder = async () => {
    if (!orderId) {
      toast.error("Order ID is missing");
      return;
    }

    if (tests.length === 0) {
      toast.error("Please add at least one test");
      return;
    }

    setIsSavingTests(true);

    try {
      const token = sessionStorage.getItem("usr_1a2b3c");

      // Format tests for API
      const formattedTests = tests.map((test) => ({
        testName: test.testName,
        serviceCode: test.serviceCode,
        price: parseFloat(test.price),
        location: test.location,
        transportationFee: parseFloat(test.transportationFee || 0),
      }));

      const response = await axios.post(
        `${APP_PATH.addOrderTests}/${orderId}/tests`,
        {
          tests: formattedTests,
          transportationFee: tests.some((t) => t.location === "HOME_VISIT")
            ? parseFloat(orderTransportationFee) || 0
            : 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.status) {
        toast.success(response.data.message || "Tests saved successfully");

        // Check if split required
        if (response.data.data?.splitRequired) {
          toast.info(
            "Mixed locations detected - patient confirmation required",
          );
        }

        // Clear tests list after successful save
        setTests([]);

        // TODO: Refresh order details to show updated invoice
      } else {
        toast.error(response.data?.message || "Failed to save tests");
      }
    } catch (error) {
      console.error("Error saving tests:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save tests";
      toast.error(errorMessage);
    } finally {
      setIsSavingTests(false);
    }
  };

  // Helper function to safely parse price values that might contain commas
  const parsePrice = (priceValue) => {
    if (!priceValue) return 0;
    // Convert to string and remove commas, then parse
    const cleanPrice = String(priceValue).replace(/,/g, "");
    const parsed = parseFloat(cleanPrice);
    return isNaN(parsed) ? 0 : parsed;
  };

  const calculateInvoiceTotal = () => {
    if (!tests || tests.length === 0) {
      return {
        subtotal: 0,
        transportationFees: 0,
        adminFee: 0,
        commission: 0,
        total: 0,
        labPayout: 0,
        admin_fee_percentage: adminFeePercentage,
        consultation_fee_percentage: consultationFeePercentage,
      };
    }

    const subtotal = tests.reduce((sum, test) => {
      const price = parsePrice(test.price);
      return sum + price;
    }, 0);

    const transportationFees = tests.some((t) => t.location === "HOME_VISIT")
      ? parseFloat(orderTransportationFee) || 0
      : 0;

    // Use admin fee and consultation fee percentages from state
    console.log("FEES_DEBUG - orderDetails:", orderDetails);
    console.log("FEES_DEBUG - adminFeePercentage state:", adminFeePercentage);
    console.log(
      "FEES_DEBUG - consultationFeePercentage state:",
      consultationFeePercentage,
    );

    const finalAdminFeePercentage =
      orderDetails?.invoice?.admin_fee_percentage ||
      orderDetails?.admin_fee_percentage ||
      adminFeePercentage;
    const finalConsultationFeePercentage =
      orderDetails?.invoice?.consultation_fee_percentage ||
      consultationFeePercentage;

    console.log(
      "FEES_DEBUG - Final admin fee percentage used:",
      finalAdminFeePercentage,
    );
    console.log(
      "FEES_DEBUG - Final consultation fee percentage used:",
      finalConsultationFeePercentage,
    );

    const adminFee =
      (subtotal + transportationFees) * (finalAdminFeePercentage / 100);
    // NOTE: Commission is NO LONGER calculated on the frontend. It is now
    // computed server-side per service (Simple/Special × Insured/Non-Insured)
    // using the dynamic admin commission matrix and posted directly to the
    // admin/lab wallets at payment time. We keep the field on the returned
    // invoice as null so any consumer that reads `invoice.commission`
    // gracefully sees no commission instead of a stale calculated value.

    // Patient pays subtotal + delivery + admin fee (admin fee is calculated
    // on subtotal + delivery, not subtotal alone).
    const total = subtotal + transportationFees + adminFee;

    console.log(
      "COMMISSION_DEBUG - Calculation breakdown (commission deferred to backend):",
      {
        subtotal,
        transportationFees,
        adminFee,
        total: "Patient pays",
      },
    );

    return {
      subtotal,
      transportationFees,
      adminFee,
      commission: null, // Calculated server-side per service; not displayed
      total,
      labPayout: null, // Calculated server-side; not displayed
      admin_fee_percentage: finalAdminFeePercentage,
      consultation_fee_percentage: finalConsultationFeePercentage,
    };
  };

  // Whether the Test Details + Invoice sections should be visible. A newly
  // received order must first be Accepted (or Rejected) by the center. Until it
  // is accepted only the Accept/Reject buttons are shown; if it is rejected the
  // Test Details + Invoice sections stay hidden. Once accepted (or the order has
  // progressed past the decision step) the sections appear.
  const isAwaitingCenterDecision =
    orderDetails?.status === "REQUEST_SUBMITTED" &&
    orderDetails?.labAccepted !== true &&
    !orderDetails?.patientConfirmationReceivedAt;
  const isOrderRejected = orderDetails?.status === "REJECTED";
  // Reason the officer gave when rejecting, shown in the "Order rejected" panel.
  const orderRejectionReason =
    orderDetails?.rejectionReason || orderDetails?.rejection_reason || "";
  const showTestAndInvoiceSections =
    !!orderDetails && !isAwaitingCenterDecision && !isOrderRejected;

  // Service Date / Time can only be edited after the center accepts the order,
  // and before scheduling is locked in by a later status. Until acceptance
  // (labAccepted flag) the inputs stay disabled.
  const serviceScheduleLockedStatuses = [
    "INVOICE_RECEIVED",
    "AWAITING_PAYMENT",
    "ORDER_ACCEPTED",
    "SCHEDULED",
    "TECHNICIAN_ASSIGNED",
    "TECHNICIAN_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
    "REJECTED",
  ];
  const isServiceDateTimeDisabled =
    orderDetails?.labAccepted !== true ||
    serviceScheduleLockedStatuses.includes(orderDetails?.status);

  // The invoice has been sent to the patient: the order is waiting for payment
  // or has moved past it. From here on the service date / time are fixed, so the
  // operating-hours warnings (closed / outside hours / no time slots) are no
  // longer shown, even if the center later changes its operating hours.
  const invoiceSentStatuses = [
    "INVOICE_RECEIVED",
    "AWAITING_PAYMENT",
    "ORDER_ACCEPTED",
    "SCHEDULED",
    "TECHNICIAN_ASSIGNED",
    "TECHNICIAN_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
  ];
  const isInvoiceSentToPatient = invoiceSentStatuses.includes(
    orderDetails?.status,
  );
  // The service date / time can no longer change: the invoice was sent, or the
  // order is closed as REJECTED (by the center, or automatically when the patient
  // did not pay within the payment window after the invoice was sent). The
  // operating-hours warnings ask the officer to pick another date / time, which
  // is not possible here, so they stay hidden even if the hours change later.
  const isServiceScheduleFixed = isInvoiceSentToPatient || isOrderRejected;
  // Invoice sent and still waiting for the patient to pay: shown in the blue
  // "Invoice sent" panel at the top of the Order Details tab.
  const isAwaitingPatientPayment = ["INVOICE_RECEIVED", "AWAITING_PAYMENT"].includes(
    orderDetails?.status,
  );

  // Use backend invoice data if available, otherwise calculate locally
  const backendInvoice = orderDetails?.invoice;
  const calculatedInvoice = calculateInvoiceTotal();

  // If we have backend invoice data, use it but normalize the property names
  // The backend invoice is the source of truth once generated. Use nullish
  // coalescing (??) — NOT || — so a legitimate backend value of 0 (e.g. no admin
  // fee, no transportation fee) is respected instead of falling back to the locally
  // calculated default (which would otherwise re-introduce a phantom admin fee
  // that isn't part of the backend total).
  // Loyalty points the patient redeemed, persisted on the booking and returned on both the
  // order and its `invoice` block (the gateway only backfills older bookings from payment
  // metadata). Shown as a deduction so this breakdown's Total matches the printed invoice
  // and the amount the patient was actually charged.
  const loyaltyDiscount = Number(
    backendInvoice?.loyalty_discount ?? orderDetails?.loyaltyDiscount ?? 0,
  );
  const loyaltyPointsUsed = Number(
    backendInvoice?.loyalty_points_used ?? orderDetails?.loyaltyPointsUsed ?? 0,
  );
  // The other deductions supplied by the gateway's order-details enrichment: the promo
  // code discount and, for an INSURANCE-method payment, the insurance deduction applied at
  // checkout. Each gets its own row so the Total below adds up (LabInvoiceModal does the same).
  const promoDiscount = Number(backendInvoice?.promo_discount || 0);
  const promoCode = backendInvoice?.promo_code || null;
  const insuranceCoverage = Number(backendInvoice?.insurance_coverage || 0);

  const invoice = backendInvoice
    ? {
        subtotal: backendInvoice.subtotal ?? calculatedInvoice.subtotal,
        transportationFees:
          backendInvoice.transportation_fee ?? calculatedInvoice.transportationFees,
        adminFee: backendInvoice.admin_fee ?? calculatedInvoice.adminFee,
        commission:
          backendInvoice.consultation_fee ??
          backendInvoice.commission ??
          calculatedInvoice.commission,
        // The backend `total` is gross (subtotal + delivery + admin). The patient's Total is
        // DERIVED from it and the deduction rows below — matching LabInvoiceModal and the
        // doctor invoice — rather than taken from the payment's `amount_paid`, which is net
        // of a promo / insurance deduction that had no row and so did not add up.
        total: Math.max(
          0,
          (backendInvoice.total ?? calculatedInvoice.total) -
            insuranceCoverage -
            loyaltyDiscount -
            promoDiscount,
        ),
        labPayout: backendInvoice.lab_payout ?? calculatedInvoice.labPayout,
        admin_fee_percentage:
          backendInvoice.admin_fee_percentage ??
          calculatedInvoice.admin_fee_percentage,
        consultation_fee_percentage:
          backendInvoice.consultation_fee_percentage ??
          calculatedInvoice.consultation_fee_percentage,
      }
    : {
        ...calculatedInvoice,
        total: Math.max(0, calculatedInvoice.total - loyaltyDiscount),
      };

  console.log("FEES_DEBUG:", {
    hasBackendInvoice: !!backendInvoice,
    backendAdminFeePercentage: backendInvoice?.admin_fee_percentage,
    backendAdminFee: backendInvoice?.admin_fee,
    backendConsultationFeePercentage:
      backendInvoice?.consultation_fee_percentage,
    backendCommission:
      backendInvoice?.consultation_fee || backendInvoice?.commission,
    orderAdminFeePercentage: orderDetails?.admin_fee_percentage,
    calculatedAdminFeePercentage:
      orderDetails?.invoice?.admin_fee_percentage ||
      orderDetails?.admin_fee_percentage ||
      adminFeePercentage,
    calculatedConsultationFeePercentage: consultationFeePercentage,
    finalInvoiceAdminFeePercentage: invoice.admin_fee_percentage,
    finalInvoiceConsultationFeePercentage: invoice.consultation_fee_percentage,
    patientPays: invoice.total,
    labReceives: invoice.labPayout,
  });

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    // Convert to display format
    const statusDisplayMap = {
      pending: "Pending",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      cancelled: "Cancelled",
      reschedule_requested: "Reschedule Requested",
      "reschedule-requested": "Reschedule Requested",
      // Lab order specific status mappings
      REQUEST_SUBMITTED: "RECEIVED",
      request_submitted: "RECEIVED",
      INVOICE_RECEIVED: "Awaiting Payment",
      invoice_received: "Awaiting Payment",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    // Check uppercase version for lab order statuses
    if (statusDisplayMap[status]) {
      return statusDisplayMap[status];
    }

    // Capitalize first letter of each word for other statuses
    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format
    const statusMap = {
      Pending: "pending",
      Upcoming: "upcoming",
      Ongoing: "ongoing",
      Completed: "completed",
      Cancelled: "cancelled",
      "Reschedule Requested": "reschedule_requested",
      reschedule_requested: "reschedule_requested",
      pending: "pending",
      upcoming: "upcoming",
      ongoing: "ongoing",
      completed: "completed",
      cancelled: "cancelled",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
  ) => {
    // Use override values if provided (including null), otherwise use current state
    // undefined means "use state value", null means "explicitly clear/no filter"
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const statusToUse =
      statusOverride !== undefined ? statusOverride : statusFilter;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;

    const formattedDate = formatDate(dateToUse);
    const normalizedStatus = normalizeStatusForAPI(statusToUse);

    // Build params object - only include defined values
    const params = {
      page: pageNum,
      limit: itemsPerPage,
    };

    // Only add date if it's a valid formatted date string (not null/undefined/empty)
    if (
      formattedDate &&
      formattedDate !== null &&
      formattedDate !== "null" &&
      formattedDate !== ""
    ) {
      params.date = formattedDate;
    }

    if (searchToUse && searchToUse.trim()) {
      params.search = searchToUse.trim();
    }

    if (normalizedStatus) {
      params.status = normalizedStatus;
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchAppointmentsWithFilters(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setStatusFilter("All");
    setDate(null);
    setCurrentPage(1);
    setActiveTab("All"); // Also reset the active tab

    // Immediately fetch with reset values (pass them directly to avoid async state update issue)
    // Explicitly pass null for date to ensure it's cleared
    fetchAppointmentsWithFilters(1, "All", null, "");
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  // Initial load with default filters
  useEffect(() => {
    fetchAppointmentsWithFilters(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   dispatch(fetchDoctorAppointmentsReducer()).then((res)=>{
  //   setTotalPages(res?.payload?.pagination?.total_pages)
  // })

  //   // dispatch(joinDoctorAppointmentReducer({appointmentId:2}))
  // }, []);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchAppointmentsWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) {
      fetchAppointmentsWithFilters(prevPage);
    }
  };

  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };
  const tabs = (() => {
    const orderStatus = orderDetails?.status;
    const isOnSite = isOnSiteOrder;

    console.log("Tab visibility check - Order Status:", orderStatus);

    // On-site bookings: show Technician Detail tab for all active/in-progress/completed statuses
    const onsiteTechnicianTabStatuses = [
      "ORDER_ACCEPTED",
      "TECHNICIAN_ASSIGNED",
      "IN_PROGRESS",
      "COMPLETED",
    ];
    // Home visit bookings: show Technician Detail tab for scheduled / dispatched /
    // in-progress / completed statuses (read-only once the visit is completed).
    const homeVisitTechnicianTabStatuses = [
      "ORDER_ACCEPTED",
      "TECHNICIAN_ASSIGNED",
      "SCHEDULED",
      "TECHNICIAN_DISPATCHED",
      "IN_PROGRESS",
      "COMPLETED",
    ];

    const showTechnicianTab = isOnSite
      ? onsiteTechnicianTabStatuses.includes(orderStatus)
      : homeVisitTechnicianTabStatuses.includes(orderStatus);

    // For COMPLETED home-visit, show Technician Detail (read-only) + Results & Reports
    if (orderStatus === "COMPLETED" && !isOnSite) {
      console.log(
        "Showing Order Details, Technician Detail and Results & Reports tabs (COMPLETED home-visit status)",
      );
      return ["Order Details", "Technician Detail", "Results & Reports"];
    }

    // For COMPLETED on-site, show Technician Detail and Results & Reports
    if (orderStatus === "COMPLETED" && isOnSite) {
      return ["Order Details", "Technician Detail", "Results & Reports"];
    }

    // Show technician tab for applicable statuses
    if (showTechnicianTab) {
      console.log("Showing Order Details and Technician Detail tabs");
      return ["Order Details", "Technician Detail"];
    }

    // For all other statuses, show only Order Details
    console.log("Showing Order Details tab only");
    return ["Order Details"];
  })();

  const orderManagementTabs = [
    "Received",
    "Awaiting Patient Confirmation",
    "Generate Invoice",
    "Awaiting Payment",
    "Order Accepted",
    "Scheduled",
    "In-Progress",
    "Completed",
    "Rejected",
  ];

  const receivedTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: null,
      testType: "Lab",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: null,
      testType: "Lab",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
  ];

  const awaitingPatientConfirmationTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
  ];

  const generateInvoiceTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      testName: "CBC",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-cod";
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "On-Site":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      default:
        return "";
    }
  };

  const EditIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M11.9525 3.04199H13.25C13.4157 3.04199 13.5747 3.10784 13.6919 3.22505C13.8091 3.34226 13.875 3.50123 13.875 3.66699C13.875 3.83275 13.8091 3.99172 13.6919 4.10893C13.5747 4.22614 13.4157 4.29199 13.25 4.29199H12C10.0183 4.29199 8.59496 4.29366 7.51163 4.43866C6.44579 4.58199 5.80329 4.85533 5.32829 5.32949C4.85413 5.80449 4.58163 6.44616 4.43829 7.51199C4.29329 8.59532 4.29163 10.0187 4.29163 12.0003C4.29163 13.982 4.29329 15.4053 4.43829 16.4887C4.58163 17.5545 4.85496 18.197 5.32913 18.672C5.80413 19.1462 6.44579 19.4187 7.51163 19.562C8.59496 19.707 10.0183 19.7087 12 19.7087C13.9816 19.7087 15.405 19.707 16.4883 19.562C17.5541 19.4187 18.1966 19.1453 18.6716 18.6712C19.1458 18.1962 19.4183 17.5545 19.5616 16.4887C19.7066 15.4053 19.7083 13.982 19.7083 12.0003V10.7503C19.7083 10.5846 19.7741 10.4256 19.8913 10.3084C20.0086 10.1912 20.1675 10.1253 20.3333 10.1253C20.4991 10.1253 20.658 10.1912 20.7752 10.3084C20.8924 10.4256 20.9583 10.5846 20.9583 10.7503V12.0478C20.9583 13.972 20.9583 15.4795 20.8 16.6562C20.6383 17.8595 20.3 18.8095 19.555 19.5553C18.8091 20.3012 17.8591 20.6387 16.655 20.8003C15.4791 20.9587 13.9716 20.9587 12.0475 20.9587H11.9525C10.0283 20.9587 8.52079 20.9587 7.34413 20.8003C6.14079 20.6387 5.19079 20.3003 4.44496 19.5553C3.69913 18.8095 3.36163 17.8595 3.19996 16.6553C3.04163 15.4795 3.04163 13.972 3.04163 12.0478V11.9528C3.04163 10.0287 3.04163 8.52116 3.19996 7.34449C3.36163 6.14116 3.69996 5.19116 4.44496 4.44533C5.19079 3.69949 6.14079 3.36199 7.34496 3.20033C8.52079 3.04199 10.0283 3.04199 11.9525 3.04199ZM15.975 3.89699C16.5249 3.3622 17.2633 3.06536 18.0303 3.07068C18.7974 3.076 19.5316 3.38304 20.074 3.92541C20.6165 4.46777 20.9237 5.20187 20.9292 5.96895C20.9347 6.73603 20.638 7.47444 20.1033 8.02449L14.5633 13.5653C14.2541 13.8737 14.06 14.0687 13.8441 14.237C13.5894 14.436 13.3136 14.6065 13.0216 14.7453C12.7741 14.8628 12.5133 14.9495 12.0991 15.0878L9.67829 15.8945C9.45927 15.9674 9.22427 15.978 8.99961 15.9249C8.77494 15.8718 8.56949 15.7573 8.40626 15.594C8.24302 15.4308 8.12846 15.2253 8.07539 15.0007C8.02232 14.776 8.03285 14.541 8.10579 14.322L8.91246 11.9012C9.05079 11.4862 9.13746 11.2262 9.25496 10.9787C9.39496 10.6864 9.5644 10.4123 9.76329 10.1562C9.93163 9.93949 10.1258 9.74616 10.435 9.43699L15.975 3.89699ZM19.2191 4.78033C18.9061 4.46747 18.4817 4.29172 18.0391 4.29172C17.5966 4.29172 17.1721 4.46747 16.8591 4.78033L16.5458 5.09449C16.5636 5.17505 16.5908 5.26894 16.6275 5.37616C16.7466 5.72033 16.9733 6.17366 17.4 6.60033C17.8154 7.01596 18.3359 7.31115 18.9058 7.45449L19.2191 7.14116C19.532 6.82815 19.7077 6.40371 19.7077 5.96116C19.7077 5.5186 19.532 5.09333 19.2191 4.78033ZM17.9208 8.44033C17.3961 8.21408 16.9192 7.88998 16.5158 7.48533C16.1111 7.08187 15.787 6.60504 15.5608 6.08033L11.3483 10.292C11.0008 10.6395 10.865 10.777 10.7483 10.9253C10.6066 11.1093 10.4846 11.3076 10.3841 11.517C10.3025 11.6862 10.2408 11.8695 10.085 12.3353L9.72579 13.4145L10.5858 14.2753L11.665 13.9153C12.1316 13.7595 12.3141 13.6978 12.4841 13.617C12.6941 13.517 12.8913 13.395 13.0758 13.2512C13.2233 13.1353 13.3608 12.9995 13.7083 12.6528L17.9208 8.44033Z"
        fill="#053345"
      />
    </svg>
  );

  const testData = [
    {
      id: 1,
      testName: "Urinalysis",
      location: "Home Visit",
      price: "7,000",
    },
    {
      id: 2,
      testName: "Vitamin D",
      location: "Home Visit",
      price: "7,000",
    },
    {
      id: 3,
      testName: "Complete Blood Count (CBC)",
      location: "Home Visit",
      price: "7,000",
    },
  ];

  return (
    <LabDashboardLayout
      currentPage="lab_ordermanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <DialogReschedule
        open={openDialogReschedule}
        onClose={() => setOpenDialogReschedule(false)}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogCreateFollowUp
        open={openDialogCreateFollowUp}
        onClose={() => {
          setOpenDialogCreateFollowUp(false);
        }}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogManageAvailability
        open={openDialogManageAvailability}
        onClose={() => {
          setOpenDialogManageAvailability(false);
        }}
        children={undefined}
      />
      <DialogPatientRegistration
        open={openDialogPatientRegistration}
        onClose={() => {
          setOpenDialogPatientRegistration(false);
        }}
        children={undefined}
      />

      {showDetails ? (
        <AppointmentDetails
          handleBack={() => setShowDetails(false)}
          homeVisit={false}
          selectedAppointment={selectedAppointment}
        />
      ) : (
        <>
          <div className={styles["page-header"]}>
            <FaArrowLeftLong
              onClick={() => {
                // Navigate back to the source page
                if (source === "dashboard") {
                  navigate("/lab_dashboard");
                } else {
                  navigate("/lab_ordermanagement");
                }
              }}
              style={{
                cursor: "pointer",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#007bff")}
              onMouseLeave={(e) => (e.target.style.color = "")}
            />
            <h2>
              {t(
                "lab.appointmentDetail.appointmentDetail",
                "Appointment Detail",
              )}
            </h2>
          </div>
          <div className={styles["apt-appointments-section"]}>
            <div className={`${styles["apt-section-header"]} mb-2`}>
              <h2>
                {t("lab.appointmentDetail.appointmentId", "Appointment ID:")}{" "}
                <b>
                  {orderDetails?.bookingNumber ||
                    orderDetails?.id ||
                    t("lab.appointmentDetail.loading", "Loading...")}
                </b>{" "}
                {orderDetails?.status && (
                  <span className={styles["tag-received"]}>
                    {formatStatusText(orderDetails.status)}
                  </span>
                )}{" "}
                {orderDetails?.isUrgent && (
                  <span className={styles["tag-urgent"]}>Urgent</span>
                )}
              </h2>
              <div className={styles["apt-top-actions"]}>
                {/* {activeTab === "Order Details" && (
                  <button className={styles["exportBtn"]}>
                    <FiDownload /> Export
                  </button>
                )} */}

                <div style={{ display: "inline-flex", alignItems: "center" }}>
                  {/* Center-action SLA — visible only while awaiting the lab's action.
                      Once the invoice is sent (status leaves the waiting set) it hides. */}
                  <SlaTimer
                    variant="detail"
                    dueAt={orderDetails?.slaDueAt}
                    isUrgent={orderDetails?.slaIsUrgent}
                    status={orderDetails?.status}
                    visibleStatuses={["REQUEST_SUBMITTED"]}
                  />
                </div>

                <button
                  type="button"
                  className={styles["wallet-withdraw-btn"]}
                  onClick={() => setShowInvoice(true)}
                  style={
                    [
                      "ORDER_ACCEPTED",
                      "SCHEDULED",
                      "IN_PROGRESS",
                      "COMPLETED",
                    ].includes(orderDetails?.status)
                      ? {}
                      : { display: "none" }
                  }
                >
                  <FiPrinter size={16} />
                  {t("lab.appointmentDetail.printInvoice", "Print Invoice")}
                </button>

                <button
                  className={styles["chat-icon-btn"]}
                  onClick={toggleChat}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0C4.477 0 0 4.477 0 10C0 11.6 0.376 13.112 1.043 14.453C1.221 14.809 1.28 15.216 1.177 15.601L0.582 17.827C0.52307 18.0473 0.523124 18.2792 0.582155 18.4995C0.641186 18.7197 0.757118 18.9206 0.918315 19.0819C1.07951 19.2432 1.2803 19.3593 1.50053 19.4184C1.72075 19.4776 1.95267 19.4778 2.173 19.419L4.399 18.823C4.78538 18.7254 5.19414 18.7727 5.548 18.956C6.93095 19.6446 8.45512 20.002 10 20ZM6 11.25C5.80109 11.25 5.61032 11.329 5.46967 11.4697C5.32902 11.6103 5.25 11.8011 5.25 12C5.25 12.1989 5.32902 12.3897 5.46967 12.5303C5.61032 12.671 5.80109 12.75 6 12.75H11.5C11.6989 12.75 11.8897 12.671 12.0303 12.5303C12.171 12.3897 12.25 12.1989 12.25 12C12.25 11.8011 12.171 11.6103 12.0303 11.4697C11.8897 11.329 11.6989 11.25 11.5 11.25H6ZM5.25 8.5C5.25 8.30109 5.32902 8.11032 5.46967 7.96967C5.61032 7.82902 5.80109 7.75 6 7.75H14C14.1989 7.75 14.3897 7.82902 14.5303 7.96967C14.671 8.11032 14.75 8.30109 14.75 8.5C14.75 8.69891 14.671 8.88968 14.5303 9.03033C14.3897 9.17098 14.1989 9.25 14 9.25H6C5.80109 9.25 5.61032 9.17098 5.46967 9.03033C5.32902 8.88968 5.25 8.69891 5.25 8.5Z"
                      fill="#2B7FFF"
                    />
                  </svg>
                </button>

                {isChatOpen && (
                  <Chat
                    onClose={() => setIsChatOpen(false)}
                    patientUserId={
                      orderDetails?.patient_id ||
                      orderDetails?.patient?.user_id ||
                      orderDetails?.patient?.id
                    }
                    bookingId={orderDetails?.booking_id || orderDetails?.id}
                    title={
                      [
                        orderDetails?.patient?.full_name,
                        orderDetails?.patient?.surname,
                      ]
                        .filter(Boolean)
                        .join(" ") || "Chat"
                    }
                    subtitle={
                      orderDetails?.bookingNumber ||
                      orderDetails?.booking_number ||
                      ""
                    }
                    avatar={
                      orderDetails?.patient?.avatar_presigned_url ||
                      orderDetails?.patient?.avatar
                    }
                  />
                )}
              </div>
            </div>

            <div className={styles["patient-appointment-tabs"]}>
              {tabs.map((tab) => {
                const isVisitCenter = isOnSiteOrder;
                const tabLabelMap = {
                  "Order Details": t(
                    "lab.appointmentDetail.tabOrderDetails",
                    "Order Details",
                  ),
                  "Technician Detail":
                    tab === "Technician Detail" && isVisitCenter
                      ? t("lab.appointmentDetail.tabProcessing", "Processing")
                      : t(
                          "lab.appointmentDetail.tabTechnicianDetail",
                          "Technician Detail",
                        ),
                  "Results & Reports": t(
                    "lab.appointmentDetail.tabResultsReports",
                    "Results & Reports",
                  ),
                };
                const tabLabel = tabLabelMap[tab] || tab;
                return (
                  <button
                    key={tab}
                    className={`${styles["patient-tab"]} ${
                      activeTab === tab ? styles["active"] : ""
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tabLabel}
                  </button>
                );
              })}
            </div>

            {activeTab === "Order Details" && (
              <div className={styles["patient-tab-content"]}>
                {/* Acceptance required: a newly received order must be accepted
                    (or rejected) before the center can add tests, set the
                    service date/time or send the invoice. Shown first so the
                    decision is the first thing the officer sees. */}
                {isAwaitingCenterDecision && (
                  <section
                    className={styles["acceptance-panel"]}
                    aria-labelledby="acceptance-panel-title"
                  >
                    <div className={styles["acceptance-panel-body"]}>
                      <span
                        className={styles["acceptance-panel-icon"]}
                        aria-hidden="true"
                      >
                        <FaInfoCircle size={16} />
                      </span>
                      <div>
                        <h3
                          id="acceptance-panel-title"
                          className={styles["acceptance-panel-title"]}
                        >
                          {t(
                            "lab.appointmentDetail.acceptanceRequiredTitle",
                            "Acceptance required",
                          )}
                        </h3>
                        <p className={styles["acceptance-panel-text"]}>
                          {t(
                            "lab.appointmentDetail.acceptanceRequiredMessage",
                            "This is a new order request. Review the patient details, prescription and documents below, then accept or reject it. You can't add tests, set the service date and time, or send the invoice until the order is accepted.",
                          )}
                        </p>
                        <ul className={styles["acceptance-panel-list"]}>
                          <li>
                            <strong>
                              {t("lab.appointmentDetail.accept", "Accept")}:
                            </strong>{" "}
                            {t(
                              "lab.appointmentDetail.acceptanceAcceptHelp",
                              "your center takes this order. The patient is notified and you can continue with tests and the invoice.",
                            )}
                          </li>
                          <li>
                            <strong>
                              {t("lab.appointmentDetail.reject", "Reject")}:
                            </strong>{" "}
                            {t(
                              "lab.appointmentDetail.acceptanceRejectHelp",
                              "the order is declined and moved to the Rejected tab. The patient is notified.",
                            )}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div
                      className={`${dialogStyles["labDialog_wrapper"]} ${styles["acceptance-panel-actions"]}`}
                    >
                      <button
                        type="button"
                        className={`${dialogStyles["btn-1"]} ${styles["acceptance-panel-btn"]}`}
                        onClick={() => setShowAcceptModal(true)}
                        // Same height as the outlined Reject button.
                        style={{ border: "1px solid transparent" }}
                      >
                        <Check size={16} aria-hidden="true" />{" "}
                        {t("lab.appointmentDetail.acceptOrderTitle", "Accept Order")}
                      </button>
                      <button
                        type="button"
                        className={`${dialogStyles["btn-outlined-1"]} ${styles["acceptance-panel-btn"]}`}
                        onClick={() => {
                          setRejectReason("");
                          setShowRejectModal(true);
                        }}
                      >
                        {t("lab.appointmentDetail.rejectOrderTitle", "Reject Order")}
                      </button>
                    </div>
                  </section>
                )}

                {/* Order rejected: the red counterpart of the "Acceptance
                    required" panel, in the same place. States that the order
                    was rejected and shows the reason the officer gave. */}
                {isOrderRejected && (
                  <section
                    className={styles["rejection-panel"]}
                    aria-labelledby="rejection-panel-title"
                  >
                    <span
                      className={styles["rejection-panel-icon"]}
                      aria-hidden="true"
                    >
                      <IoClose size={18} />
                    </span>
                    <div className={styles["rejection-panel-body"]}>
                      <h3
                        id="rejection-panel-title"
                        className={styles["rejection-panel-title"]}
                      >
                        {t("lab.appointmentDetail.rejectedTitle", "Order rejected")}
                      </h3>
                      <p className={styles["rejection-panel-text"]}>
                        {t(
                          "lab.appointmentDetail.rejectedMessage",
                          "This order was rejected and the patient has been notified.",
                        )}
                      </p>
                      <div className={styles["rejection-panel-reason"]}>
                        <span className={styles["rejection-panel-reason-label"]}>
                          {t(
                            "lab.appointmentDetail.rejectionReasonLabel",
                            "Reason for rejection",
                          )}
                        </span>
                        {orderRejectionReason ? (
                          <p className={styles["rejection-panel-reason-text"]}>
                            {orderRejectionReason}
                          </p>
                        ) : (
                          <p
                            className={`${styles["rejection-panel-reason-text"]} ${styles["rejection-panel-reason-empty"]}`}
                          >
                            {t(
                              "lab.appointmentDetail.noRejectionReason",
                              "No reason was given.",
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                )}

                {/* Invoice sent: the blue counterpart of the panels above, in
                    the same place. Shown while the invoice is with the patient
                    and the payment is still pending. */}
                {isAwaitingPatientPayment && (
                  <section
                    className={styles["invoice-sent-panel"]}
                    aria-labelledby="invoice-sent-panel-title"
                  >
                    <span
                      className={styles["invoice-sent-panel-icon"]}
                      aria-hidden="true"
                    >
                      <FaFileInvoice size={15} />
                    </span>
                    <div className={styles["invoice-sent-panel-body"]}>
                      <h3
                        id="invoice-sent-panel-title"
                        className={styles["invoice-sent-panel-title"]}
                      >
                        {t("lab.appointmentDetail.invoiceSentTitle", "Invoice sent")}
                      </h3>
                      <p className={styles["invoice-sent-panel-text"]}>
                        {t(
                          "lab.appointmentDetail.statusInvoiceSentWaitingPayment",
                          "The invoice has been sent to the patient. Waiting for the patient to complete the payment.",
                        )}
                      </p>
                    </div>
                  </section>
                )}

                <div className={styles["white-card"]}>
                  <div
                    className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                  >
                    <h2>
                      {t(
                        "lab.appointmentDetail.patientInformation",
                        "Patient Information",
                      )}
                    </h2>
                  </div>

                  {/* Out-of-operating-hours banner: shown when the patient's
                      preferred date/slot falls outside the center's operating
                      hours (closed day, period out of hours, or already passed). */}
                  {(() => {
                    // Once the schedule is fixed (invoice sent or order rejected), a
                    // later change to the operating hours no longer raises this banner.
                    if (isServiceScheduleFixed) return null;
                    const banner = buildSlotBanner();
                    if (!banner || banner.key === dismissedBannerKey)
                      return null;
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          background: "#FEF2F2",
                          border: "1px solid #FECACA",
                          borderRadius: "10px",
                          padding: "12px 14px",
                          margin: "0 0 16px",
                        }}
                      >
                        <span
                          style={{
                            flexShrink: 0,
                            width: "26px",
                            height: "26px",
                            borderRadius: "50%",
                            background: "#FEE2E2",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#DC2626",
                          }}
                        >
                          <FaClock style={{ fontSize: "13px" }} />
                        </span>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                            flex: 1,
                          }}
                        >
                          <span
                            style={{
                              color: "#B91C1C",
                              fontSize: "13px",
                              fontWeight: 700,
                            }}
                          >
                            {banner.title}
                          </span>
                          <span
                            style={{
                              color: "#7F1D1D",
                              fontSize: "12.5px",
                              lineHeight: 1.45,
                            }}
                          >
                            {banner.detail}
                          </span>
                        </div>
                        <button
                          type="button"
                          aria-label="Dismiss"
                          onClick={() => setDismissedBannerKey(banner.key)}
                          style={{
                            flexShrink: 0,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#B91C1C",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px",
                            lineHeight: 1,
                          }}
                        >
                          <IoClose style={{ fontSize: "16px" }} />
                        </button>
                      </div>
                    );
                  })()}

                  {loadingOrderDetails ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                      <Loader />
                      <p>
                        {t(
                          "lab.appointmentDetail.loadingOrderDetails",
                          "Loading order details...",
                        )}
                      </p>
                    </div>
                  ) : !orderDetails ? (
                    <div
                      style={{
                        padding: "40px",
                        textAlign: "center",
                        color: "#999",
                      }}
                    >
                      {t(
                        "lab.appointmentDetail.noOrderDetails",
                        "No order details available",
                      )}
                    </div>
                  ) : (
                    <>
                      <div className={styles["field-block"]}>
                        <div className={`${styles["label"]} mb-2`}>
                          {t(
                            "lab.appointmentDetail.patientName",
                            "Patient Name",
                          )}
                        </div>
                        <div className={styles["user-section"]}>
                          <div className={styles["userInfo"]}>
                            {/* No patient photo or child icon is shown (same as the doctor
                                app's appointment details); the name and "Child" badge say
                                who the patient is. */}
                            <h3>
                              {(() => {
                                // Guardian (account owner) full name — used as
                                // the primary label for self bookings and as a
                                // fallback for child bookings when the child
                                // snapshot is somehow missing.
                                const guardianName =
                                  [
                                    orderDetails?.patient?.full_name,
                                    orderDetails?.patient?.surname,
                                  ]
                                    .filter(Boolean)
                                    .join(" ") || "N/A";

                                const isChildFlow =
                                  orderDetails?.is_for_child === true ||
                                  orderDetails?.isForChild === true;

                                if (isChildFlow) {
                                  const childName =
                                    [
                                      orderDetails?.child_name ??
                                        orderDetails?.childName,
                                      orderDetails?.child_surname,
                                    ]
                                      .filter(Boolean)
                                      .join(" ") ||
                                    "";
                                  // Child flow: the clinical patient is the
                                  // child, so surface the child's name here.
                                  // The guardian is shown as a separate
                                  // "Guardian" field in the info grid below.
                                  return childName || guardianName;
                                }

                                return guardianName;
                              })()}
                              {(orderDetails?.is_for_child === true ||
                                orderDetails?.isForChild === true) && (
                                <span
                                  // Small teal pill flagging that this booking
                                  // is on behalf of a child. Matches the
                                  // corner-badge style used elsewhere in the
                                  // app (address default pill, etc.) so it
                                  // reads as a status indicator, not a button.
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                    background: "#0cbdbd",
                                    color: "#fff",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    fontWeight: 500,
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {t(
                                    "lab.appointmentDetail.childBadge",
                                    "Child",
                                  )}
                                </span>
                              )}
                            </h3>
                          </div>

                          {/* <div className={styles["msgIcon"]}>
                              <BsFillChatTextFill />
                            </div> */}
                        </div>
                      </div>

                      <div className={styles["medical-history"]}>
                        <div
                          className={`${styles["history-grid"]} ${styles["med-grid"]}`}
                        >
                          {/* Guardian — shown ONLY for the child flow. The clinical
                              patient (name/age/gender) is the child; this row surfaces
                              the guardian (account owner) so the officer can still see
                              who booked and who to contact. Placed first so it sits
                              directly above Age | Gender. */}
                          {(orderDetails?.is_for_child === true ||
                            orderDetails?.isForChild === true) && (
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>
                                {t(
                                  "lab.appointmentDetail.guardianName",
                                  "Guardian Name",
                                )}
                              </div>
                              <div className={styles["value"]}>
                                {[
                                  orderDetails?.patient?.full_name,
                                  orderDetails?.patient?.surname,
                                ]
                                  .filter(Boolean)
                                  .join(" ") || "N/A"}
                              </div>
                            </div>
                          )}

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.ageGender",
                                "Age | Gender",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              {(() => {
                                // Child flow: compute age from
                                // `child_date_of_birth` (live, so the record
                                // stays accurate even years later) and format
                                // the gender as Title Case. Self flow: use the
                                // pre-computed patient age + gender straight
                                // from the payload as before.
                                const isChildFlow =
                                  orderDetails?.is_for_child === true ||
                                  orderDetails?.isForChild === true;
                                if (isChildFlow) {
                                  const dob =
                                    orderDetails?.child_date_of_birth ??
                                    orderDetails?.childDateOfBirth;
                                  let ageLabel = "N/A";
                                  if (dob) {
                                    const birth = new Date(dob);
                                    if (!isNaN(birth.getTime())) {
                                      const now = new Date();
                                      let years =
                                        now.getFullYear() -
                                        birth.getFullYear();
                                      const monthDiff =
                                        now.getMonth() - birth.getMonth();
                                      if (
                                        monthDiff < 0 ||
                                        (monthDiff === 0 &&
                                          now.getDate() < birth.getDate())
                                      ) {
                                        years -= 1;
                                      }
                                      if (years >= 0) ageLabel = `${years}`;
                                    }
                                  }
                                  const rawGender =
                                    orderDetails?.child_gender ??
                                    orderDetails?.childGender;
                                  const genderLabel = rawGender
                                    ? rawGender.charAt(0).toUpperCase() +
                                      rawGender.slice(1).toLowerCase()
                                    : "N/A";
                                  return `${ageLabel} yr | ${genderLabel}`;
                                }
                                return `${orderDetails?.patient?.age || "N/A"} yr | ${orderDetails?.patient?.gender || "N/A"}`;
                              })()}
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.mobileNumber",
                                "Mobile Number",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              {orderDetails?.patient?.mobile_number || "N/A"}
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.preferredTechnicianGender",
                                "Preferred Technician Gender",
                              )}{" "}
                            </div>
                            <div className={styles["value"]}>
                              {orderDetails?.preferredTechnicianGender || "N/A"}
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.serviceType",
                                "Service Type",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              <span className={styles["badge-lab"]}>
                                {orderDetails?.serviceType || "N/A"}
                              </span>
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.preferredLocation",
                                "Preferred Location",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              <span
                                className={
                                  orderDetails?.preferredLocation ===
                                  "Visit Center"
                                    ? styles["badge-onsite"]
                                    : styles["badge-lab"]
                                }
                              >
                                {orderDetails?.preferredLocation ===
                                "Visit Center"
                                  ? t("lab.appointmentDetail.onSite", "On-Site")
                                  : orderDetails?.preferredLocation || "N/A"}
                              </span>
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.preferredDate",
                                "Preferred Date",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              {orderDetails?.preferredDate
                                ? tzCustom(new Date(orderDetails.preferredDate), {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                    .toUpperCase()
                                : "N/A"}
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.preferredTimeSlot",
                                "Preferred Time Slot",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              {orderDetails?.preferredTimeSlot
                                ? (() => {
                                    const periodKey = getPreferredPeriodKey();
                                    const periodName = periodKey
                                      ? periodKey.charAt(0) +
                                        periodKey.slice(1).toLowerCase()
                                      : null;
                                    // Closure/out-of-hours state is already surfaced by the
                                    // dismissable banner at the top of the page (see
                                    // `buildSlotBanner`), so we no longer duplicate that here
                                    // as an inline red pill next to the period name — the
                                    // field just shows the operating-hours-clipped range when
                                    // available, otherwise the generic band as a fallback.
                                    const win = getPreferredSlotWindow();
                                    const staticRange =
                                      periodKey === "MORNING"
                                        ? "8:00 AM - 12:00 PM"
                                        : periodKey === "AFTERNOON"
                                          ? "12:00 PM - 5:00 PM"
                                          : periodKey === "EVENING"
                                            ? "5:00 PM - 7:30 PM"
                                            : null;
                                    const rangeText = win
                                      ? `${minutesToLabel12(win.startMin)} - ${minutesToLabel12(win.endMin)}`
                                      : staticRange;
                                    if (!periodName)
                                      return orderDetails.preferredTimeSlot;
                                    return rangeText
                                      ? `${periodName} (${rangeText})`
                                      : periodName;
                                  })()
                                : "N/A"}
                            </div>
                          </div>

                          <div className={styles["field-block"]}>
                            <div className={styles["label"]}>
                              {t(
                                "lab.appointmentDetail.paymentMethod",
                                "Payment Method",
                              )}
                            </div>
                            <div className={styles["value"]}>
                              {orderDetails?.paymentMethod ? (
                                <span className={styles["badge-cod"]}>
                                  {orderDetails.paymentMethod}
                                </span>
                              ) : (
                                "N/A"
                              )}
                            </div>
                          </div>

                          {/* Insurance Status field hidden by product request.
                              The value is still emitted by the backend and is
                              consumed by the Insurance summary card further
                              down when the booking actually has an insurance
                              claim; only the top-level "Patient Information"
                              row is suppressed here to reduce noise on the
                              detail page. */}
                        </div>
                      </div>
                    </>
                  )}

                  <div className={styles["medical-history"]}>
                    <div
                      className={`${styles["history-grid"]} ${styles["med-grid"]}`}
                    >
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t("lab.appointmentDetail.ageGender", "Age | Gender")}
                        </div>
                        <div className={styles["value"]}>
                          {/* Describes the same person as the header: the child on a
                              child booking (as the block above already does), the
                              account holder otherwise. */}
                          {isChildBooking(orderDetails) ? (
                            <>
                              {ageFromDob(getBookingChildDob(orderDetails)) ??
                                "N/A"}{" "}
                              yr |{" "}
                              {titleCaseGender(
                                getBookingChildGender(orderDetails),
                              ) || "N/A"}
                            </>
                          ) : (
                            <>
                              {orderDetails?.patient?.age || "N/A"} yr |{" "}
                              {orderDetails?.patient?.gender || "N/A"}
                            </>
                          )}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.mobileNumber",
                            "Mobile Number",
                          )}
                        </div>
                        <div className={styles["value"]}>
                          {orderDetails?.patient?.mobile_number || "N/A"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.preferredTechnicianGender",
                            "Preferred Technician Gender",
                          )}{" "}
                        </div>
                        <div className={styles["value"]}>
                          {orderDetails?.preferredTechnicianGender || "N/A"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.serviceType",
                            "Service Type",
                          )}
                        </div>
                        <div className={styles["value"]}>
                          <span className={styles["badge-lab"]}>
                            {orderDetails?.serviceType || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.serviceLocation",
                            "Service Location",
                          )}
                        </div>
                        <div className={styles["value"]}>
                          <span
                            className={
                              orderDetails?.serviceLocation === "Visit Center"
                                ? styles["badge-onsite"]
                                : styles["badge-lab"]
                            }
                          >
                            {orderDetails?.serviceLocation === "Visit Center"
                              ? t("lab.appointmentDetail.onSite", "On-Site")
                              : orderDetails?.serviceLocation || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.serviceDate",
                            "Service Date",
                          )}
                          <span
                            style={{ color: "#EF4444", marginLeft: 2 }}
                            aria-hidden="true"
                          >
                            *
                          </span>
                        </div>
                        <div className={styles["date-wrapper"]}>
                          <DatePicker
                            className={styles["apt-date-filter"]}
                            selected={serviceDate}
                            onChange={(d) => {
                              dateSelectedByUserRef.current = true;
                              setServiceDate(d);
                              setServiceTimeTipDismissed(false);
                            }}
                            placeholderText={
                              orderDetails?.scheduledDate
                                ? tzDateNumeric(new Date(
                                    orderDetails.scheduledDate,
                                  ))
                                : "Select service date"
                            }
                            dateFormat="dd/MM/yyyy"
                            minDate={minServiceDate}
                            disabled={isServiceDateTimeDisabled}
                          />
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.serviceTime",
                            "Service Time",
                          )}
                          <span
                            style={{ color: "#EF4444", marginLeft: 2 }}
                            aria-hidden="true"
                          >
                            *
                          </span>
                        </div>
                        <div className={styles["value"]}>
                          <div className={styles["apt-field-wrapper"]}>
                            <RxClock className={styles["apt-field-icon"]} />
                            <select
                              value={serviceTime}
                              onChange={(e) => setServiceTime(e.target.value)}
                              className={styles["apt-field-input"]}
                              disabled={isServiceDateTimeDisabled}
                              style={{
                                appearance: "none",
                                paddingRight: "30px",
                                cursor: isServiceDateTimeDisabled
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              <option value="">
                                {orderDetails?.scheduledTime || "Select time"}
                              </option>
                              {timeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <RxChevronDown
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#9CA3AF",
                              }}
                            />
                          </div>
                        </div>
                        {/* Center is open but the preferred period has no bookable slots
                            for this date (outside operating hours, or all slots already
                            passed today) — shown as a dismissible tooltip on an amber chip. */}
                        {(() => {
                          // Not shown once the schedule is fixed (invoice sent or order rejected).
                          if (isServiceScheduleFixed) return null;
                          if (!serviceDate || !orderDetails?.preferredTimeSlot)
                            return null;
                          const selStr = formatDateYMD(serviceDate);
                          if (!centerSlots || centerSlots.date !== selStr)
                            return null;
                          if (centerSlots.is_open === false) return null; // handled by the closed banner/toast
                          if (timeOptions.length > 0) return null;
                          if (serviceTimeTipDismissed) return null;
                          const periodKey = getPreferredPeriodKey();
                          const periodName = periodKey
                            ? periodKey.charAt(0) +
                              periodKey.slice(1).toLowerCase()
                            : String(orderDetails.preferredTimeSlot);
                          return (
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "flex-start",
                                marginTop: "6px",
                                background: "#FFFBEB",
                                border: "1px solid #FDE68A",
                                borderRadius: "8px",
                                padding: "8px 10px",
                              }}
                            >
                              <FaInfoCircle
                                style={{
                                  color: "#D97706",
                                  marginTop: "2px",
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  color: "#92400E",
                                  fontSize: "12px",
                                  lineHeight: 1.45,
                                  flex: 1,
                                }}
                              >
                                No available time slots for the {periodName}{" "}
                                slot on this date. Please choose another date.
                              </span>
                              <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setServiceTimeTipDismissed(true)}
                                style={{
                                  flexShrink: 0,
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#B45309",
                                  display: "inline-flex",
                                  padding: 0,
                                }}
                              >
                                <IoClose style={{ fontSize: "15px" }} />
                              </button>
                            </div>
                          );
                        })()}
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.paymentMethod",
                            "Payment Method",
                          )}
                        </div>
                        <div className={styles["value"]}>
                          {orderDetails?.paymentMethod ? (
                            <span className={styles["badge-cod"]}>
                              {orderDetails.paymentMethod}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          {t(
                            "lab.appointmentDetail.paymentStatus",
                            "Payment Status",
                          )}
                        </div>
                        <div className={styles["value"]}>
                          {orderDetails?.paymentStatus ? (
                            orderDetails.paymentStatus === "REFUNDED" ||
                            orderDetails.paymentStatus ===
                              "PARTIALLY_REFUNDED" ? (
                              <span
                                className={styles["badge-failed"]}
                                style={{
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                }}
                              >
                                {orderDetails.paymentStatus ===
                                "PARTIALLY_REFUNDED"
                                  ? t(
                                      "lab.appointmentDetail.paymentPartiallyRefunded",
                                      "Partially Refunded",
                                    )
                                  : t(
                                      "lab.appointmentDetail.paymentRefunded",
                                      "Refunded",
                                    )}
                              </span>
                            ) : (
                              <span
                                className={
                                  orderDetails.paymentStatus === "COMPLETED"
                                    ? styles["badge-paid"]
                                    : orderDetails.paymentStatus === "FAILED"
                                      ? styles["badge-failed"]
                                      : styles["badge-cod"]
                                }
                              >
                                {orderDetails.paymentStatus === "COMPLETED"
                                  ? t(
                                      "lab.appointmentDetail.paymentComplete",
                                      "Payment Complete",
                                    )
                                  : orderDetails.paymentStatus === "PENDING"
                                    ? t(
                                        "lab.appointmentDetail.paymentPending",
                                        "Pending",
                                      )
                                    : orderDetails.paymentStatus === "FAILED"
                                      ? t(
                                          "lab.appointmentDetail.paymentFailed",
                                          "Failed",
                                        )
                                      : orderDetails.paymentStatus}
                              </span>
                            )
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`row mb-3 pb-1 ${styles["details-grid"]} `}>
                  <div className={`col-lg-6 ${styles["details-grid-leftCol"]}`}>
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>
                          {t("lab.appointmentDetail.caseNote", "Case Note")}
                        </h2>
                      </div>

                      <div className={styles["grid-col-4"]}>
                        <div className={styles["field-block"]}>
                          <div
                            className={
                              styles["value"] +
                              " " +
                              styles["font-medium"] +
                              " " +
                              styles["address-box"]
                            }
                          >
                            {orderDetails?.caseNotes || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`${styles["white-card"]} ${styles["address-card"]} mb-0`}
                    >
                      <div className={styles["patient-tab-title"]}>
                        <h2>
                          {t(
                            "lab.appointmentDetail.patientAddress",
                            "Patient Address",
                          )}
                        </h2>
                      </div>

                      <div className={styles["grid-col-4"]}>
                        <div
                          className={`${styles["field-block"]} ${styles["address-field"]}`}
                        >
                          <div
                            className={
                              styles["value"] +
                              " " +
                              styles["font-medium"] +
                              " " +
                              styles["address-box"]
                            }
                          >
                            {orderDetails?.patientAddress ? (
                              <>
                                <LiaMapMarkerSolid />
                                {orderDetails.patientAddress.houseFlatFloorNo ||
                                  "N/A"}
                                ,
                                <br />
                                {orderDetails.patientAddress.street || "N/A"},
                                <br />
                                {orderDetails.patientAddress.areaDistrict ||
                                  "N/A"}
                                ,
                                <br />
                                {orderDetails.patientAddress.city ||
                                  "N/A"},{" "}
                                {orderDetails.patientAddress.governorate ||
                                  "N/A"}
                                <br />
                                {orderDetails.patientAddress.country ||
                                  "N/A"} -{" "}
                                {orderDetails.patientAddress.postalCode ||
                                  "N/A"}
                              </>
                            ) : (
                              <>
                                <LiaMapMarkerSolid /> N/A
                              </>
                            )}
                          </div>
                          {(() => {
                            const addr = orderDetails?.patientAddress;
                            const hasCoords =
                              addr &&
                              addr.latitude != null &&
                              addr.latitude !== "" &&
                              addr.longitude != null &&
                              addr.longitude !== "" &&
                              !isNaN(Number(addr.latitude)) &&
                              !isNaN(Number(addr.longitude));
                            const textAddress = addr
                              ? [
                                  addr.houseFlatFloorNo,
                                  addr.street,
                                  addr.areaDistrict,
                                  addr.city,
                                  addr.governorate,
                                  addr.country,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : "";
                            const destination = hasCoords
                              ? `${addr.latitude},${addr.longitude}`
                              : textAddress;
                            const mapsUrl = destination
                              ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving&dir_action=navigate`
                              : null;
                            return mapsUrl ? (
                              <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles["maps-link"]}
                              >
                                {t(
                                  "lab.appointmentDetail.openInMaps",
                                  "Open in Maps",
                                )}
                              </a>
                            ) : (
                              <span
                                className={`${styles["maps-link"]} ${styles["maps-link-disabled"]}`}
                              >
                                {t(
                                  "lab.appointmentDetail.openInMaps",
                                  "Open in Maps",
                                )}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div
                      className={`${styles["white-card"]} ${styles["prescription-card"]}`}
                    >
                      <div className={styles["patient-tab-title"]}>
                        <h2>
                          {t(
                            "lab.appointmentDetail.prescription",
                            "Prescription",
                          )}
                        </h2>
                      </div>
                      <div>
                        <div className={styles["medical-history"]}>
                          {(() => {
                            const prescriptionDocs =
                              orderDetails?.documents?.filter(
                                (doc) => doc.document_type === "PRESCRIPTION",
                              ) || [];

                            if (prescriptionDocs.length > 0) {
                              return prescriptionDocs.map((doc, index) => {
                                const docUrl =
                                  doc.presignedUrl || doc.presigned_url;
                                // A presigned URL is generated even for a missing S3 object, so rely on
                                // the backend's file_available HEAD check to decide whether the file is
                                // actually there. When unavailable, show a clear "not uploaded" state.
                                const fileAvailable =
                                  doc.file_available !== false && !!docUrl;
                                return (
                                  <div
                                    key={doc.id || index}
                                    className={styles["upload-row"]}
                                  >
                                    <div
                                      className={
                                        styles["upload-item"] +
                                        " " +
                                        styles["w-100"]
                                      }
                                    >
                                      <div className={styles["file-name"]}>
                                        <AiOutlineFilePdf
                                          size={30}
                                          className="text-danger"
                                        />
                                        <span className="d-flex flex-column">
                                          <strong>
                                            {doc.file_name ||
                                              t(
                                                "lab.appointmentDetail.prescriptionDocument",
                                                "Prescription Document",
                                              )}
                                          </strong>
                                          <span>
                                            {t(
                                              "lab.appointmentDetail.uploaded",
                                              "Uploaded:",
                                            )}{" "}
                                            {doc.uploaded_at
                                              ? tzCustom(new Date(
                                                  doc.uploaded_at,
                                                ), {
                                                  month: "short",
                                                  day: "numeric",
                                                  year: "numeric",
                                                }) +
                                                " at " +
                                                tzCustom(new Date(
                                                  doc.uploaded_at,
                                                ), {
                                                  hour: "numeric",
                                                  minute: "2-digit",
                                                  hour12: true,
                                                })
                                              : "N/A"}
                                          </span>
                                        </span>
                                      </div>
                                      {fileAvailable && (
                                        <a
                                          href={docUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            cursor: "pointer",
                                            textDecoration: "none",
                                            color: "inherit",
                                          }}
                                        >
                                          <RxChevronDown
                                            size={24}
                                            className="text-secondary"
                                          />
                                        </a>
                                      )}
                                    </div>
                                    {fileAvailable ? (
                                      <div
                                        className={styles["preview-scroller"]}
                                      >
                                        <div
                                          className={styles["uploaded-preview"]}
                                        >
                                          <div
                                            className={
                                              styles["preview-wrapper"]
                                            }
                                          >
                                            <iframe
                                              src={docUrl}
                                              style={{
                                                width: "100%",
                                                height: "250px",
                                                border: "none",
                                                borderRadius: "8px",
                                              }}
                                              title={`Prescription ${index + 1}`}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          width: "100%",
                                          boxSizing: "border-box",
                                          border: "1px dashed #FCA5A5",
                                          borderRadius: "8px",
                                          backgroundColor: "#FEF2F2",
                                          padding: "32px 16px",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          textAlign: "center",
                                          gap: "6px",
                                          marginTop: "8px",
                                        }}
                                      >
                                        <AiOutlineFilePdf
                                          size={34}
                                          style={{
                                            color: "#B91C1C",
                                            opacity: 0.6,
                                          }}
                                        />
                                        <div
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#B91C1C",
                                          }}
                                        >
                                          {t(
                                            "lab.appointmentDetail.prescriptionNotUploaded",
                                            "Prescription not uploaded properly",
                                          )}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 12,
                                            color: "#6b7280",
                                            maxWidth: 360,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {t(
                                            "lab.appointmentDetail.prescriptionNotUploadedDesc",
                                            "We couldn't load this prescription. It may not have been uploaded correctly or the file is no longer available. Please ask the patient to re-upload it.",
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                            } else {
                              return (
                                <div className="text-center py-4">
                                  <AiOutlineFilePdf
                                    size={48}
                                    className="text-muted mb-2"
                                  />
                                  <p className="text-muted mb-0">
                                    {t(
                                      "lab.appointmentDetail.noPrescriptionFound",
                                      "No prescription found",
                                    )}
                                  </p>
                                  <small className="text-muted">
                                    {t(
                                      "lab.appointmentDetail.noPrescriptionUploaded",
                                      "No prescription has been uploaded for this order",
                                    )}
                                  </small>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* medical document */}
                <div className={styles["white-card"]}>
                  <div className={styles["patient-tab-title"]}>
                    <h2>
                      {t(
                        "lab.appointmentDetail.medicalDocument",
                        "Medical Document",
                      )}
                    </h2>
                  </div>
                  <div>
                    <div
                      className={styles["medical-history"]}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        padding: "12px 4px",
                      }}
                    >
                      {(() => {
                        const medicalDocs =
                          orderDetails?.documents?.filter(
                            (doc) => doc.document_type === "MEDICAL_DOCUMENT",
                          ) || [];

                        if (medicalDocs.length === 0) {
                          return (
                            <div className="p-3 text-secondary fw-semibold">
                              {t(
                                "lab.appointmentDetail.noMedicalDocument",
                                "No medical document uploaded",
                              )}
                            </div>
                          );
                        }

                        return medicalDocs.map((doc, index) => {
                          const fileUrl = doc.presignedUrl || doc.file_url;
                          return (
                            <div
                              key={doc.id || index}
                              className={styles["upload-row"]}
                            >
                              <a
                                className={
                                  styles["upload-item"] + " " + styles["w-100"]
                                }
                                href={fileUrl || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  cursor: fileUrl ? "pointer" : "default",
                                  textDecoration: "none",
                                  color: "inherit",
                                }}
                              >
                                <div className={styles["file-name"]}>
                                  <AiOutlineFilePdf
                                    size={30}
                                    className="text-danger"
                                  />
                                  <span>
                                    {doc.file_name ||
                                      t(
                                        "lab.appointmentDetail.medicalDocumentFile",
                                        "Medical Document",
                                      )}
                                  </span>
                                </div>
                              </a>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>

                {showTestAndInvoiceSections && (
                  <>
                    {/* test details  */}
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>
                          {t(
                            "lab.appointmentDetail.testDetails",
                            "Test Details",
                          )}
                        </h2>

                        {orderDetails?.status === "REQUEST_SUBMITTED" &&
                          orderDetails?.labAccepted === true &&
                          !orderDetails?.patientConfirmationReceivedAt && (
                            <button
                              onClick={() => {
                                const location = getEffectiveLocation();
                                setCurrentTest((prev) => ({
                                  ...prev,
                                  location,
                                  transportationFee:
                                    location === LOCATION_CODES.HOME_VISIT
                                      ? HOME_VISIT_DEFAULT_TRANSPORTATION_FEE
                                      : "0",
                                }));
                                setShowAddTestRow(true);
                              }}
                              className={styles["add-test-link"]}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <FaCirclePlus />{" "}
                              {t("lab.appointmentDetail.addTest", "Add Test")}
                            </button>
                          )}
                      </div>
                      <div className={styles["bg-gray-light"] + " p-2"}>
                        {/* <div className={styles["col-block"]}>
                      <div className={styles["test-card"]}>
                        <div className={styles["test-header"]}>
                          <div className={styles["test-title"]}>TEST</div>
                          <div className={styles["test-btns-wrapper"]}>
                            <button className={styles["test-edit-btn"]}>
                              <EditIcon />
                            </button>
                            <div className={styles["test-delete"]}>
                              <Trash2 />
                            </div>
                          </div>
                        </div>

                        <div className={`${styles["test-row"]} py-1`}>
                          <div className={styles["test-label"]}>Test</div>
                          <span className={styles["test-value-strong"]}>
                            Urinalysis
                          </span>
                        </div>

                        <div className={`${styles["test-row"]} py-1`}>
                          <span className={styles["test-label"]}>
                            Service Code
                          </span>
                          <span className={styles["test-value-strong"]}>
                            #001
                          </span>
                        </div>

                        <div className={`${styles["test-row"]} py-1`}>
                          <span className={styles["test-label"]}>Price</span>
                          <span className={styles["test-value-strong"]}>
                            IQD 64,000
                          </span>
                        </div>

                        <div className={`${styles["test-row"]} py-1`}>
                          <span className={styles["test-label"]}>Location</span>
                          <div className={styles["test-locations"]}>
                            <span
                              className={
                                styles["test-pill"] +
                                " " +
                                styles["test-onsite"]
                              }
                            >
                              On-Site
                            </span>

                            <span
                              className={
                                styles["test-pill"] + " " + styles["test-home"]
                              }
                            >
                              Home Visit
                            </span>
                          </div>
                        </div>

                        <div className={`${styles["test-row"]} py-1`}>
                          <span className={styles["test-label"]}>
                            Transportation Fee
                          </span>
                          <span className={styles["test-value-strong"]}>
                            IQD 0
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={styles["col-block"]}>
                      <div className={styles["test-card"]}>
                        <div className={styles["test-header"]}>
                          <div className={styles["test-title"]}>TEST</div>
                          <button className={styles["test-save-btn"]}>
                            Save
                          </button>
                        </div>

                        <div className={styles["test-row"]}>
                          <div className={styles["test-label"]}>Test</div>
                          <select className={styles["test-select"]}>
                            <option value="">Select test</option>
                            <option value="test1">Test 1</option>
                            <option value="test2">Test 2</option>
                          </select>
                        </div>

                        <div className={styles["test-row"]}>
                          <span className={styles["test-label"]}>
                            Service Code
                          </span>
                          <span className={styles["test-value-strong"]}>
                            #002
                          </span>
                        </div>

                        <div className={styles["test-row"]}>
                          <span className={styles["test-label"]}>Price</span>
                          <input type="text" className={styles["test-input"]} />
                        </div>

                        <div className={styles["test-row"]}>
                          <span className={styles["test-label"]}>Location</span>
                          <select className={styles["test-select"]}>
                            <option value="">On-Site</option>
                            <option value="test1">Test 1</option>
                            <option value="test2">Test 2</option>
                          </select>
                        </div>

                        <div className={styles["test-row"]}>
                          <span className={styles["test-label"]}>
                            Transportation Fee
                          </span>
                          <input type="text" className={styles["test-input"]} />
                        </div>
                      </div>
                    </div> */}

                        <div
                          className={`${styles["apt-appointments-table"]} ${styles["test-details-table"]}`}
                        >
                          <table>
                            <thead>
                              <tr>
                                <th>
                                  {t(
                                    "lab.appointmentDetail.tableTestName",
                                    "Test Name",
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "lab.appointmentDetail.tableLocation",
                                    "Location",
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "lab.appointmentDetail.tablePrice",
                                    "Price",
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "lab.appointmentDetail.tableAction",
                                    "Action",
                                  )}
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {loading ? (
                                <tr>
                                  <td colSpan={11}>
                                    <Loader />
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {tests.length === 0 && !showAddTestRow ? (
                                    <tr>
                                      <td
                                        colSpan={5}
                                        style={{
                                          textAlign: "center",
                                          padding: "20px",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {t(
                                          "lab.appointmentDetail.pleaseAddATest",
                                          "Please add a test",
                                        )}
                                      </td>
                                    </tr>
                                  ) : (
                                    <>
                                      {tests
                                        .filter(
                                          (test) =>
                                            test && test.testName && test.price,
                                        )
                                        .map((item, idx) => {
                                          return editingTestId === item.id ? (
                                            // Edit mode row
                                            <tr key={idx}>
                                              <td>
                                                <TestSearchSelect
                                                  services={
                                                    availableLabServicesForEdit
                                                  }
                                                  value={editingTestServiceId}
                                                  onSelect={
                                                    handleEditTestSelection
                                                  }
                                                  loading={loadingLabServices}
                                                  placeholder={t(
                                                    "lab.appointmentDetail.searchByTestNameOrCode",
                                                    "Search by test name or code",
                                                  )}
                                                />
                                              </td>
                                              <td>
                                                <select
                                                  value={editingTest.location}
                                                  onChange={(e) => {
                                                    const newLocation =
                                                      e.target.value;
                                                    const preferredLocation =
                                                      orderDetails?.preferredLocation;

                                                    // Normalize preferred location values
                                                    const isPreferredOnSite =
                                                      normalizeLocationCode(
                                                        preferredLocation,
                                                      ) ===
                                                      LOCATION_CODES.ON_SITE;

                                                    // Prevent changing to HOME_VISIT if preferred location is On-Site
                                                    if (
                                                      isPreferredOnSite &&
                                                      newLocation ===
                                                        LOCATION_CODES.HOME_VISIT
                                                    ) {
                                                      toast.warning(
                                                        "Cannot change to Home Visit when preferred location is On-Site",
                                                      );
                                                      return;
                                                    }

                                                    // A location change applies to every test in the order.
                                                    applyLocationToAllTests(
                                                      newLocation,
                                                    );
                                                  }}
                                                  className={
                                                    styles["apt-status-filter"]
                                                  }
                                                >
                                                  {allowedLocations.includes(
                                                    LOCATION_CODES.HOME_VISIT,
                                                  ) && (
                                                    <option value="HOME_VISIT">
                                                      {t(
                                                        "lab.appointmentDetail.homeVisit",
                                                        "Home Visit",
                                                      )}
                                                    </option>
                                                  )}
                                                  {allowedLocations.includes(
                                                    LOCATION_CODES.ON_SITE,
                                                  ) && (
                                                    <option value="ON_SITE">
                                                      {t(
                                                        "lab.appointmentDetail.onSiteOption",
                                                        "On-site",
                                                      )}
                                                    </option>
                                                  )}
                                                </select>
                                              </td>
                                              <td>
                                                <AmountInput
                                                  placeholder={t(
                                                    "lab.appointmentDetail.pricePlaceholder",
                                                    "IQD 80,000",
                                                  )}
                                                  value={editingTest.price}
                                                  onValueChange={(value) =>
                                                    handleEditTestInputChange(
                                                      "price",
                                                      value,
                                                    )
                                                  }
                                                  className={
                                                    styles["apt-field-input"]
                                                  }
                                                />
                                              </td>
                                              <td>
                                                <div
                                                  className={
                                                    styles["apt-action-buttons"]
                                                  }
                                                >
                                                  <button
                                                    className={
                                                      styles["apt-view-btn"]
                                                    }
                                                    title={t(
                                                      "lab.appointmentDetail.save",
                                                      "Save",
                                                    )}
                                                    onClick={saveEditedTest}
                                                  >
                                                    <svg
                                                      width="24"
                                                      height="24"
                                                      viewBox="0 0 24 24"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path
                                                        d="M15.7489 10.1671C15.8174 10.1032 15.8724 10.0262 15.9105 9.94061C15.9487 9.85503 15.9692 9.76264 15.9708 9.66897C15.9725 9.57529 15.9553 9.48224 15.9202 9.39537C15.8851 9.30849 15.8328 9.22958 15.7666 9.16333C15.7003 9.09708 15.6214 9.04485 15.5346 9.00976C15.4477 8.97467 15.3546 8.95744 15.261 8.95909C15.1673 8.96075 15.0749 8.98125 14.9893 9.01938C14.9037 9.05751 14.8267 9.11249 14.7628 9.18104L10.6047 13.3392L9.23726 11.9717C9.105 11.8485 8.93008 11.7814 8.74933 11.7846C8.56859 11.7878 8.39613 11.861 8.26831 11.9888C8.14048 12.1167 8.06726 12.2891 8.06407 12.4699C8.06088 12.6506 8.12798 12.8255 8.25121 12.9578L10.1117 14.8182C10.2425 14.9489 10.4198 15.0223 10.6047 15.0223C10.7896 15.0223 10.9669 14.9489 11.0977 14.8182L15.7489 10.1671Z"
                                                        fill="#10B981"
                                                      />
                                                      <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z"
                                                        fill="#10B981"
                                                      />
                                                    </svg>
                                                  </button>
                                                  <button
                                                    className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                                    title={t(
                                                      "lab.appointmentDetail.cancel",
                                                      "Cancel",
                                                    )}
                                                    onClick={cancelEditingTest}
                                                  >
                                                    <svg
                                                      width="24"
                                                      height="24"
                                                      viewBox="0 0 24 24"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path
                                                        d="M10.1676 9.18104C10.0353 9.0578 9.86039 8.99071 9.67965 8.9939C9.4989 8.99709 9.32645 9.07031 9.19862 9.19814C9.0708 9.32596 8.99758 9.49841 8.99439 9.67916C8.9912 9.8599 9.05829 10.0348 9.18153 10.1671L11.0141 11.9996L9.18153 13.8322C9.11298 13.8961 9.058 13.9731 9.01987 14.0587C8.98174 14.1443 8.96123 14.2366 8.95958 14.3303C8.95793 14.424 8.97516 14.517 9.01025 14.6039C9.04534 14.6908 9.09757 14.7697 9.16382 14.836C9.23007 14.9022 9.30898 14.9544 9.39585 14.9895C9.48273 15.0246 9.57578 15.0418 9.66945 15.0402C9.76313 15.0385 9.85552 15.018 9.9411 14.9799C10.0267 14.9418 10.1037 14.8868 10.1676 14.8182L12.0001 12.9857L13.8327 14.8182C13.9649 14.9415 14.1399 15.0086 14.3206 15.0054C14.5014 15.0022 14.6738 14.929 14.8016 14.8012C14.9295 14.6733 15.0027 14.5009 15.0059 14.3201C15.0091 14.1394 14.942 13.9645 14.8187 13.8322L12.9862 11.9996L14.8187 10.1671C14.8873 10.1032 14.9423 10.0262 14.9804 9.94061C15.0185 9.85503 15.039 9.76264 15.0407 9.66897C15.0423 9.57529 15.0251 9.48224 14.99 9.39537C14.9549 9.30849 14.9027 9.22958 14.8364 9.16333C14.7702 9.09708 14.6913 9.04485 14.6044 9.00976C14.5175 8.97467 14.4245 8.95744 14.3308 8.95909C14.2371 8.96075 14.1447 8.98125 14.0592 9.01938C13.9736 9.05751 13.8966 9.11249 13.8327 9.18104L12.0001 11.0136L10.1676 9.18104Z"
                                                        fill="#EF4444"
                                                      />
                                                      <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z"
                                                        fill="#EF4444"
                                                      />
                                                    </svg>
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          ) : (
                                            // View mode row
                                            <tr key={idx}>
                                              <td>
                                                {item.testName || "N/A"}
                                              </td>
                                              <td>
                                                <span
                                                  className={`${styles["db-status"]} ${
                                                    styles[
                                                      getStatusColor(
                                                        item.location,
                                                      )
                                                    ]
                                                  }`}
                                                >
                                                  {item.location ===
                                                  "HOME_VISIT"
                                                    ? t(
                                                        "lab.appointmentDetail.homeVisit",
                                                        "Home Visit",
                                                      )
                                                    : t(
                                                        "lab.appointmentDetail.onSite",
                                                        "On-Site",
                                                      )}
                                                </span>
                                              </td>
                                              <td>
                                                IQD{" "}
                                                {parsePrice(
                                                  item.price,
                                                ).toLocaleString("en-US", {
                                                  minimumFractionDigits: 0,
                                                  maximumFractionDigits: 0,
                                                })}
                                              </td>

                                              {/* ACTION COLUMN */}
                                              <td>
                                                {(() => {
                                                  const isEditable =
                                                    orderDetails?.status ===
                                                      "REQUEST_SUBMITTED" &&
                                                    !orderDetails?.patientConfirmationReceivedAt;
                                                  const disabledStyle =
                                                    !isEditable
                                                      ? {
                                                          opacity: 0.5,
                                                          cursor: "not-allowed",
                                                        }
                                                      : {};
                                                  return (
                                                    <div
                                                      className={
                                                        styles[
                                                          "apt-action-buttons"
                                                        ]
                                                      }
                                                    >
                                                      <button
                                                        className={
                                                          styles["apt-view-btn"]
                                                        }
                                                        title={t(
                                                          "lab.appointmentDetail.edit",
                                                          "Edit",
                                                        )}
                                                        disabled={!isEditable}
                                                        style={disabledStyle}
                                                        onClick={() =>
                                                          isEditable &&
                                                          startEditingTest(item)
                                                        }
                                                      >
                                                        <EditIcon />
                                                      </button>
                                                      <button
                                                        className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                                        title={t(
                                                          "lab.appointmentDetail.delete",
                                                          "Delete",
                                                        )}
                                                        disabled={!isEditable}
                                                        style={disabledStyle}
                                                        onClick={() =>
                                                          isEditable &&
                                                          removeTestFromList(
                                                            item.id,
                                                          )
                                                        }
                                                      >
                                                        <Trash2 size={24} />
                                                      </button>
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                            </tr>
                                          );
                                        })}

                                      {showAddTestRow && (
                                        <tr>
                                          <td>
                                            <TestSearchSelect
                                              services={availableLabServices}
                                              value={selectedTestId}
                                              onSelect={handleTestSelection}
                                              loading={loadingLabServices}
                                              placeholder={t(
                                                "lab.appointmentDetail.searchByTestNameOrCode",
                                                "Search by test name or code",
                                              )}
                                            />
                                          </td>
                                          <td>
                                            <select
                                              value={currentTest.location}
                                              onChange={(e) => {
                                                const newLocation =
                                                  e.target.value;
                                                const preferredLocation =
                                                  orderDetails?.preferredLocation;

                                                const isPreferredOnSite =
                                                  normalizeLocationCode(
                                                    preferredLocation,
                                                  ) === LOCATION_CODES.ON_SITE;

                                                if (
                                                  isPreferredOnSite &&
                                                  newLocation ===
                                                    LOCATION_CODES.HOME_VISIT
                                                ) {
                                                  toast.warning(
                                                    "Cannot change to Home Visit when preferred location is On-Site",
                                                  );
                                                  return;
                                                }

                                                // A location change applies to every test in the order.
                                                applyLocationToAllTests(
                                                  newLocation,
                                                );
                                              }}
                                              className={
                                                styles["apt-status-filter"]
                                              }
                                              disabled={!selectedTestId}
                                              style={
                                                !selectedTestId
                                                  ? {
                                                      backgroundColor:
                                                        "#f3f4f6",
                                                      cursor: "not-allowed",
                                                      opacity: 0.6,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {allowedLocations.includes(
                                                LOCATION_CODES.HOME_VISIT,
                                              ) && (
                                                <option value="HOME_VISIT">
                                                  {t(
                                                    "lab.appointmentDetail.homeVisit",
                                                    "Home Visit",
                                                  )}
                                                </option>
                                              )}
                                              {allowedLocations.includes(
                                                LOCATION_CODES.ON_SITE,
                                              ) && (
                                                <option value="ON_SITE">
                                                  {t(
                                                    "lab.appointmentDetail.onSiteOption",
                                                    "On-site",
                                                  )}
                                                </option>
                                              )}
                                            </select>
                                          </td>
                                          <td>
                                            <AmountInput
                                              placeholder={t(
                                                "lab.appointmentDetail.pricePlaceholder",
                                                "IQD 80,000",
                                              )}
                                              value={currentTest.price}
                                              onValueChange={(value) =>
                                                handleTestInputChange(
                                                  "price",
                                                  value,
                                                )
                                              }
                                              className={
                                                styles["apt-field-input"]
                                              }
                                              disabled={!selectedTestId}
                                              style={
                                                !selectedTestId
                                                  ? {
                                                      backgroundColor:
                                                        "#f3f4f6",
                                                      cursor: "not-allowed",
                                                      opacity: 0.6,
                                                    }
                                                  : {}
                                              }
                                            />
                                          </td>

                                          {/* ACTION COLUMN */}
                                          <td>
                                            <div
                                              className={
                                                styles["apt-action-buttons"]
                                              }
                                            >
                                              <button
                                                className={
                                                  styles["apt-view-btn"]
                                                }
                                                title={t(
                                                  "lab.appointmentDetail.addTest",
                                                  "Add Test",
                                                )}
                                                onClick={addTestToList}
                                                disabled={!selectedTestId}
                                                style={
                                                  !selectedTestId
                                                    ? {
                                                        opacity: 0.4,
                                                        cursor: "not-allowed",
                                                      }
                                                    : {}
                                                }
                                              >
                                                <svg
                                                  width="24"
                                                  height="24"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M15.7489 10.1671C15.8174 10.1032 15.8724 10.0262 15.9105 9.94061C15.9487 9.85503 15.9692 9.76264 15.9708 9.66897C15.9725 9.57529 15.9553 9.48224 15.9202 9.39537C15.8851 9.30849 15.8328 9.22958 15.7666 9.16333C15.7003 9.09708 15.6214 9.04485 15.5346 9.00976C15.4477 8.97467 15.3546 8.95744 15.261 8.95909C15.1673 8.96075 15.0749 8.98125 14.9893 9.01938C14.9037 9.05751 14.8267 9.11249 14.7628 9.18104L10.6047 13.3392L9.23726 11.9717C9.105 11.8485 8.93008 11.7814 8.74933 11.7846C8.56859 11.7878 8.39613 11.861 8.26831 11.9888C8.14048 12.1167 8.06726 12.2891 8.06407 12.4699C8.06088 12.6506 8.12798 12.8255 8.25121 12.9578L10.1117 14.8182C10.2425 14.9489 10.4198 15.0223 10.6047 15.0223C10.7896 15.0223 10.9669 14.9489 11.0977 14.8182L15.7489 10.1671Z"
                                                    fill="#10B981"
                                                  />
                                                  <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z"
                                                    fill="#10B981"
                                                  />
                                                </svg>
                                              </button>
                                              <button
                                                className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                                title="Cancel"
                                                onClick={() => {
                                                  setSelectedTestId("");
                                                  setCurrentTest({
                                                    testName: "",
                                                    serviceCode: "",
                                                    price: "",
                                                    location: "ON_SITE",
                                                    transportationFee: "0",
                                                  });
                                                  setShowAddTestRow(false);
                                                }}
                                              >
                                                <svg
                                                  width="24"
                                                  height="24"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M10.1676 9.18104C10.0353 9.0578 9.86039 8.99071 9.67965 8.9939C9.4989 8.99709 9.32645 9.07031 9.19862 9.19814C9.0708 9.32596 8.99758 9.49841 8.99439 9.67916C8.9912 9.8599 9.05829 10.0348 9.18153 10.1671L11.0141 11.9996L9.18153 13.8322C9.11298 13.8961 9.058 13.9731 9.01987 14.0587C8.98174 14.1443 8.96123 14.2366 8.95958 14.3303C8.95793 14.424 8.97516 14.517 9.01025 14.6039C9.04534 14.6908 9.09757 14.7697 9.16382 14.836C9.23007 14.9022 9.30898 14.9544 9.39585 14.9895C9.48273 15.0246 9.57578 15.0418 9.66945 15.0402C9.76313 15.0385 9.85552 15.018 9.9411 14.9799C10.0267 14.9418 10.1037 14.8868 10.1676 14.8182L12.0001 12.9857L13.8327 14.8182C13.9649 14.9415 14.1399 15.0086 14.3206 15.0054C14.5014 15.0022 14.6738 14.929 14.8016 14.8012C14.9295 14.6733 15.0027 14.5009 15.0059 14.3201C15.0091 14.1394 14.942 13.9645 14.8187 13.8322L12.9862 11.9996L14.8187 10.1671C14.8873 10.1032 14.9423 10.0262 14.9804 9.94061C15.0185 9.85503 15.039 9.76264 15.0407 9.66897C15.0423 9.57529 15.0251 9.48224 14.99 9.39537C14.9549 9.30849 14.9027 9.22958 14.8364 9.16333C14.7702 9.09708 14.6913 9.04485 14.6044 9.00976C14.5175 8.97467 14.4245 8.95744 14.3308 8.95909C14.2371 8.96075 14.1447 8.98125 14.0592 9.01938C13.9736 9.05751 13.8966 9.11249 13.8327 9.18104L12.0001 11.0136L10.1676 9.18104Z"
                                                    fill="#EF4444"
                                                  />
                                                  <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z"
                                                    fill="#EF4444"
                                                  />
                                                </svg>
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Transportation Fee - only for Home Visit tests */}
                    {(() => {
                      const displayTests =
                        tests.length > 0 ? tests : orderDetails?.tests || [];
                      const hasHomeVisitTests = displayTests.some(
                        (t) => t.location === "HOME_VISIT",
                      );
                      const isEditable =
                        orderDetails?.status === "REQUEST_SUBMITTED";
                      if (!hasHomeVisitTests || !isEditable) return null;
                      return (
                        <>
                          {!transportationFeeAdded && (
                            <div style={{ padding: "12px 20px" }}>
                              <button
                                onClick={() => {
                                  setTempTransportationFee(
                                    orderTransportationFee === "0"
                                      ? ""
                                      : orderTransportationFee,
                                  );
                                  setShowTransportationFeePopup(true);
                                }}
                                style={{
                                  background: "none",
                                  border: "2px dashed #099494",
                                  borderRadius: "8px",
                                  padding: "10px 20px",
                                  color: "#099494",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                + Add Transportation Fee
                              </button>
                            </div>
                          )}
                          {showTransportationFeePopup && (
                            <div className={dialogStyles["labDialog_wrapper"]}>
                              <div
                                className={dialogStyles["dialog-overlay"]}
                                style={{ alignItems: "center" }}
                              >
                                <div
                                  className={dialogStyles["dialog-box"]}
                                  style={{ width: "460px" }}
                                >
                                  <button
                                    className={dialogStyles["dialog-close"]}
                                    onClick={() =>
                                      setShowTransportationFeePopup(false)
                                    }
                                  >
                                    ×
                                  </button>
                                  <h2 className={dialogStyles["dialog-title"]}>
                                    {transportationFeeAdded
                                      ? "Edit Transportation Fee"
                                      : "Add Transportation Fee"}
                                  </h2>
                                  <label
                                    style={{
                                      fontSize: "14px",
                                      color: "#374151",
                                      fontWeight: 500,
                                      marginBottom: "6px",
                                      display: "block",
                                    }}
                                  >
                                    Transportation Fee (IQD)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    placeholder="Enter transportation fee"
                                    value={tempTransportationFee}
                                    onChange={(e) =>
                                      setTempTransportationFee(e.target.value)
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "10px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "12px",
                                      fontSize: "14px",
                                      outline: "none",
                                      marginBottom: "4px",
                                      boxSizing: "border-box",
                                    }}
                                    autoFocus
                                  />
                                  <div
                                    className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
                                  >
                                    <button
                                      className={dialogStyles["btn-outlined-1"]}
                                      onClick={() =>
                                        setShowTransportationFeePopup(false)
                                      }
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className={dialogStyles["btn-1"]}
                                      onClick={() => {
                                        // 0 IQD is a valid fee (free transport). Only an
                                        // empty field is refused.
                                        const raw = String(
                                          tempTransportationFee ?? "",
                                        ).trim();
                                        const fee = Number(raw);
                                        if (
                                          raw === "" ||
                                          !Number.isFinite(fee) ||
                                          fee < 0
                                        ) {
                                          toast.error(
                                            "Enter a transportation fee (0 IQD is allowed).",
                                          );
                                          return;
                                        }
                                        explicitZeroFeeRef.current = fee === 0;
                                        setOrderTransportationFee(String(fee));
                                        setTransportationFeeAdded(true);
                                        setShowTransportationFeePopup(false);
                                      }}
                                      style={{
                                        justifyContent: "center",
                                        border: "1px solid transparent",
                                        boxSizing: "border-box",
                                      }}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* invoice  */}
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>Invoice</h2>
                        {tests.length > 0 && (
                          <span style={{ fontSize: "14px", color: "#666" }}>
                            {tests.length} test(s) added
                          </span>
                        )}
                      </div>

                      {tests.length === 0 ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#999",
                          }}
                        >
                          No tests added yet. Add tests using the form above.
                        </div>
                      ) : (
                        <>
                          {tests.map((test, index) => (
                            <div
                              key={test.id}
                              className={styles["test-row"]}
                              style={{ position: "relative" }}
                            >
                              <div>
                                <span className={styles["test-label"]}>
                                  {test.testName}
                                  <span
                                    style={{
                                      marginLeft: "8px",
                                      fontSize: "12px",
                                      color: "#666",
                                      backgroundColor:
                                        test.location === "HOME_VISIT"
                                          ? "#E8F0FF"
                                          : "#F3E8FF",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {test.location === "HOME_VISIT"
                                      ? "Home Visit"
                                      : "On-Site"}
                                  </span>
                                </span>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#999",
                                    marginTop: "4px",
                                  }}
                                >
                                  Code: {test.serviceCode}
                                  {test.transportationFee > 0 &&
                                    ` | Delivery: IQD ${formatWholeAmount(test.transportationFee)}`}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <span className={styles["test-value-strong"]}>
                                  IQD {formatWholeAmount(test.price)}
                                </span>
                                {orderDetails?.status === "REQUEST_SUBMITTED" &&
                                  !orderDetails?.patientConfirmationReceivedAt && (
                                    <button
                                      onClick={() =>
                                        removeTestFromList(test.id)
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        color: "#E55353",
                                        cursor: "pointer",
                                        padding: "4px",
                                      }}
                                      title="Remove test"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                              </div>
                            </div>
                          ))}

                          <hr
                            style={{
                              margin: "16px 0",
                              border: "none",
                              borderTop: "1px solid #eee",
                            }}
                          />

                          <div className={styles["test-row"]}>
                            <span className={styles["test-label"]}>
                              {t("lab.appointmentDetail.subtotal", "Subtotal")}
                            </span>
                            <span className={styles["test-value-strong"]}>
                              IQD{" "}
                              {invoice.subtotal.toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </div>

                          {/* Shown for an added 0 IQD fee too, so it can still be edited or
                              removed, and for a home visit once the invoice has been sent
                              (a 0 fee reads "Free"). */}
                          {(invoice.transportationFees > 0 ||
                            transportationFeeAdded ||
                            (orderDetails?.status !== "REQUEST_SUBMITTED" &&
                              (tests.length > 0
                                ? tests
                                : orderDetails?.tests || []
                              ).some((t) => t.location === "HOME_VISIT"))) && (
                            <div className={styles["test-row"]}>
                              <span
                                className={styles["test-label"]}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                {t(
                                  "lab.appointmentDetail.transportationFee",
                                  "Transportation Fee",
                                )}
                                {orderDetails?.status ===
                                  "REQUEST_SUBMITTED" && (
                                  <>
                                    <button
                                      onClick={() => {
                                        setTempTransportationFee(orderTransportationFee);
                                        setShowTransportationFeePopup(true);
                                      }}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "2px",
                                        color: "#099494",
                                      }}
                                      title="Edit"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                      >
                                        <path
                                          d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => {
                                        explicitZeroFeeRef.current = false;
                                        setOrderTransportationFee("0");
                                        setTransportationFeeAdded(false);
                                      }}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "2px",
                                        color: "#EF4444",
                                      }}
                                      title="Remove"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                      >
                                        <path
                                          d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </span>
                              <span className={styles["test-value-strong"]}>
                                {Number(invoice.transportationFees) > 0
                                  ? `IQD ${Number(invoice.transportationFees).toLocaleString("en-US", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}`
                                  : t("lab.appointmentDetail.free", "Free")}
                              </span>
                            </div>
                          )}

                          {/* Only show the admin fee line when one actually applies.
                              No admin fee configured in the admin panel => no row. */}
                          {Number(invoice.adminFee) > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                Admin Fee (
                                {formatPercent(
                                  invoice.admin_fee_percentage || 0,
                                )}
                                %)
                              </span>
                              <span className={styles["test-value-strong"]}>
                                IQD{" "}
                                {Number(invoice.adminFee).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  },
                                )}
                              </span>
                            </div>
                          )}

                          {/* Insurance deduction applied at checkout (INSURANCE-method
                              payments only), same position as on the printed invoice. */}
                          {insuranceCoverage > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                {t(
                                  "lab.appointmentDetail.insuranceCoverage",
                                  "Insurance Coverage",
                                )}
                              </span>
                              <span
                                className={styles["test-value-strong"]}
                                style={{ color: "#00BC7D" }}
                              >
                                − IQD{" "}
                                {insuranceCoverage.toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </div>
                          )}

                          {/* Loyalty points redeemed — shown as a deduction (green, like
                              the printed invoice) so the Total below reconciles with what
                              the patient was actually charged. */}
                          {loyaltyDiscount > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                {t(
                                  "lab.appointmentDetail.loyaltyDiscount",
                                  "Loyalty Point Discount",
                                )}
                                {loyaltyPointsUsed > 0
                                  ? ` (${loyaltyPointsUsed} ${t("lab.appointmentDetail.points", "pts")})`
                                  : ""}
                              </span>
                              <span
                                className={styles["test-value-strong"]}
                                style={{ color: "#00BC7D" }}
                              >
                                − IQD{" "}
                                {loyaltyDiscount.toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </div>
                          )}

                          {/* Promo code discount, so the Total below still adds up. */}
                          {promoDiscount > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                {t(
                                  "lab.appointmentDetail.promoDiscount",
                                  "Promo Discount",
                                )}
                                {promoCode ? ` (${promoCode})` : ""}
                              </span>
                              <span
                                className={styles["test-value-strong"]}
                                style={{ color: "#00BC7D" }}
                              >
                                − IQD{" "}
                                {promoDiscount.toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </div>
                          )}

                          <div
                            className={
                              styles["test-row"] +
                              " " +
                              styles["total-price-row"]
                            }
                          >
                            <span className={styles["test-value-strong"]}>
                              {t(
                                "lab.appointmentDetail.totalPricePatientPays",
                                "Total Price (Patient Pays)",
                              )}
                            </span>
                            <span className={styles["test-value-strong"]}>
                              IQD{" "}
                              {invoice.total.toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </div>

                          {/* Commission Deduction / Lab Earnings Breakdown is NOT
                          shown here. Commission is calculated server-side
                          per service (Simple/Special × Insured/Non-Insured)
                          using the dynamic admin matrix and posted to the
                          admin/lab wallets at payment time. */}
                        </>
                      )}
                    </div>
                  </>
                )}

                <div className={styles["text-center"]}>{getActionButton()}</div>
              </div>
            )}

            {activeTab === "Technician Detail" && (
              <div className={`${styles["patient-tab-content"]} p-0 bg-white`}>
                <div className={styles["white-card"]}>
                  <div
                    className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                  >
                    <h2>
                      {isOnSiteOrder
                        ? t("lab.appointmentDetail.tabProcessing", "Processing")
                        : t(
                            "lab.appointmentDetail.scheduleDispatch",
                            "Schedule & Dispatch",
                          )}
                    </h2>
                    <div className={styles["patient-actions-wrap"]}></div>
                  </div>

                  <div className={styles["medical-history"]}>
                    {/* Determine if this is an On-Site booking (based on test locations) */}
                    {(() => {
                      const isOnSite = isOnSiteOrder;
                      const isCOD = orderDetails?.paymentMethod === "COD";
                      const isPaymentCompleted =
                        orderDetails?.paymentStatus === "COMPLETED";
                      const isSampleCollected =
                        orderDetails?.workflowStatus?.sampleCollected ||
                        !!orderDetails?.sampleCollectedAt ||
                        !!orderDetails?.sample_collected_at ||
                        orderDetails?.status === "COMPLETED";
                      const isJobDone = orderDetails?.status === "COMPLETED";

                      // Home-visit technician assignment state, shared by the
                      // dispatch dropdown hint, the status banner and the
                      // WhatsApp button.
                      const isTechnicianAssigned =
                        !!orderDetails?.assignedTechnicianId;
                      const canAssignTechnician =
                        orderDetails?.status === "ORDER_ACCEPTED" ||
                        orderDetails?.status === "TECHNICIAN_ASSIGNED";
                      const showAddTechnicianHint =
                        canAssignTechnician &&
                        !isTechnicianAssigned &&
                        !loadingTechnicians &&
                        !selectedTechnicianId;
                      const isSendWhatsAppDisabled =
                        loadingTechnicians ||
                        assigningTechnician ||
                        !canAssignTechnician ||
                        isTechnicianAssigned ||
                        !selectedTechnicianId;

                      // Technician Status banner. Terminal statuses win; otherwise
                      // an assigned technician counts as "assigned" even after the
                      // order moves on (SCHEDULED / IN_PROGRESS).
                      const technicianStatus =
                        orderDetails?.status === "COMPLETED"
                          ? {
                              isPending: false,
                              label: t(
                                "lab.appointmentDetail.completed",
                                "Completed",
                              ),
                            }
                          : orderDetails?.status === "TECHNICIAN_DISPATCHED"
                            ? {
                                isPending: false,
                                label: t(
                                  "lab.appointmentDetail.dispatched",
                                  "Dispatched",
                                ),
                              }
                            : isTechnicianAssigned ||
                                orderDetails?.status === "TECHNICIAN_ASSIGNED"
                              ? {
                                  isPending: false,
                                  label: t(
                                    "lab.appointmentDetail.technicianAssignedStatus",
                                    "Technician Assigned",
                                  ),
                                }
                              : {
                                  isPending: true,
                                  label: t(
                                    "lab.appointmentDetail.notAssigned",
                                    "Not Assigned",
                                  ),
                                };
                      const assignedTechnicianName =
                        orderDetails?.technician?.name ||
                        orderDetails?.assignedTechnician?.name;
                      const assignedTechnicianPhone =
                        orderDetails?.technician?.phone;

                      return (
                        <>
                          <div
                            className={`${styles["dispatch-grid-3"]} border-0`}
                          >
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>
                                {t(
                                  "lab.appointmentDetail.serviceDate",
                                  "Service Date",
                                )}
                              </div>
                              <div className={styles["value"]}>
                                {orderDetails?.scheduledDate
                                  ? tzCustom(new Date(
                                      orderDetails.scheduledDate,
                                    ), {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })
                                  : t(
                                      "lab.appointmentDetail.notScheduled",
                                      "Not scheduled",
                                    )}
                              </div>
                            </div>

                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>
                                {t(
                                  "lab.appointmentDetail.serviceTime",
                                  "Service Time",
                                )}
                              </div>
                              <div className={styles["value"]}>
                                {orderDetails?.scheduledTime
                                  ? formatTime12h(orderDetails.scheduledTime)
                                  : t(
                                      "lab.appointmentDetail.notScheduled",
                                      "Not scheduled",
                                    )}
                              </div>
                            </div>

                            {/* Hide technician gender, preferred gender, assigned technician, assign dropdown for On-Site */}
                            {!isOnSite && (
                              <>
                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    {t(
                                      "lab.appointmentDetail.technicianGender",
                                      "Technician Gender",
                                    )}
                                  </div>
                                  <div className={styles["gender-options"]}>
                                    <label className={styles["gender-option"]}>
                                      <input
                                        type="checkbox"
                                        checked={gender === "male"}
                                        onChange={() => setGender("male")}
                                        disabled={
                                          isJobDone ||
                                          !!orderDetails?.assignedTechnicianId
                                        }
                                      />
                                      <IoIosCheckbox
                                        className={styles["checkbox-icon"]}
                                        size={24}
                                      />
                                      {t("lab.appointmentDetail.male", "Male")}
                                    </label>
                                    <label className={styles["gender-option"]}>
                                      <input
                                        type="checkbox"
                                        checked={gender === "female"}
                                        onChange={() => setGender("female")}
                                        disabled={
                                          isJobDone ||
                                          !!orderDetails?.assignedTechnicianId
                                        }
                                      />
                                      <IoIosCheckbox
                                        className={styles["checkbox-icon"]}
                                        size={24}
                                      />
                                      {t(
                                        "lab.appointmentDetail.female",
                                        "Female",
                                      )}
                                    </label>
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    {t(
                                      "lab.appointmentDetail.preferredTechnicianGender",
                                      "Preferred Technician Gender",
                                    )}
                                  </div>
                                  <div className={styles["value"]}>
                                    {orderDetails?.preferredTechnicianGender ||
                                      t("lab.appointmentDetail.any", "Any")}
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    {t(
                                      "lab.appointmentDetail.assignedLabTechnician",
                                      "Assigned Lab Technician",
                                    )}
                                  </div>
                                  <div className={styles["value"]}>
                                    {orderDetails?.assignedTechnician
                                      ? `${orderDetails.assignedTechnician.name} (${orderDetails.assignedTechnician.serviceCode})`
                                      : t(
                                          "lab.appointmentDetail.notAssignedValue",
                                          "Not assigned",
                                        )}
                                  </div>
                                </div>

                                <div className={`${styles["field-block"]}`}>
                                  <div className={styles["label"]}>
                                    {t(
                                      "lab.appointmentDetail.assignLabTechnician",
                                      "Assign Lab Technician",
                                    )}
                                  </div>
                                  <div
                                    className={
                                      styles["dispatch-select-wrapper"]
                                    }
                                  >
                                    <select
                                      className={styles["dispatch-select"]}
                                      value={selectedTechnicianId}
                                      onChange={(e) =>
                                        setSelectedTechnicianId(e.target.value)
                                      }
                                      aria-describedby={
                                        showAddTechnicianHint
                                          ? "assign-technician-hint"
                                          : undefined
                                      }
                                      disabled={
                                        loadingTechnicians ||
                                        (orderDetails?.status !==
                                          "ORDER_ACCEPTED" &&
                                          orderDetails?.status !==
                                            "TECHNICIAN_ASSIGNED") ||
                                        !gender ||
                                        orderDetails?.assignedTechnicianId
                                      }
                                    >
                                      <option value="">
                                        {loadingTechnicians
                                          ? t(
                                              "lab.appointmentDetail.loadingTechnicians",
                                              "Loading technicians...",
                                            )
                                          : orderDetails?.assignedTechnicianId
                                            ? t(
                                                "lab.appointmentDetail.technicianAssignedOption",
                                                "Technician assigned",
                                              )
                                            : orderDetails?.status !==
                                                  "ORDER_ACCEPTED" &&
                                                orderDetails?.status !==
                                                  "TECHNICIAN_ASSIGNED"
                                              ? t(
                                                  "lab.appointmentDetail.paymentNotCompleted",
                                                  "Payment not completed",
                                                )
                                              : !gender
                                                ? t(
                                                    "lab.appointmentDetail.selectTechnicianGender",
                                                    "Please select technician gender",
                                                  )
                                                : t(
                                                    "lab.appointmentDetail.selectLabTechnician",
                                                    "Select lab technician",
                                                  )}
                                      </option>
                                      {availableTechnicians
                                        .filter((tech) => {
                                          if (!gender) return false;
                                          return (
                                            tech.gender.toLowerCase() ===
                                            gender.toLowerCase()
                                          );
                                        })
                                        .map((tech) => (
                                          <option
                                            key={tech.id}
                                            value={tech.id}
                                            disabled={!tech.isAvailable}
                                          >
                                            {tech.name} ({tech.serviceCode}) -{" "}
                                            {tech.gender}
                                            {!tech.isAvailable
                                              ? ` ${t("lab.appointmentDetail.unavailable", "(Unavailable)")}`
                                              : ""}
                                          </option>
                                        ))}
                                    </select>
                                    <svg
                                      className={styles["select-arrow"]}
                                      width="12"
                                      height="8"
                                      viewBox="0 0 12 8"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M1 1.5L6 6.5L11 1.5"
                                        stroke="#9CA3AF"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                  {showAddTechnicianHint && (
                                    <div
                                      id="assign-technician-hint"
                                      className={styles["field-error-text"]}
                                    >
                                      {t(
                                        "lab.appointmentDetail.addTechnicianHint",
                                        "Please add a technician.",
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          <hr />

                          {isOnSite ? (
                            /* ON-SITE WORKFLOW: Sample Collected + Mark as Completed/Collected */
                            <div>
                              <div
                                className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}
                              >
                                <h2>
                                  {t(
                                    "lab.appointmentDetail.orderActions",
                                    "Order Actions",
                                  )}
                                </h2>
                              </div>

                              {/* Sample Collected button */}
                              <button
                                className={`${styles["apt-register-btn"]} ${isSampleCollected ? styles["gray_colored"] : ""} py-2 px-3 mb-2 me-3`}
                                onClick={async () => {
                                  if (isSampleCollected || isSampleCollecting)
                                    return;
                                  setIsSampleCollecting(true);
                                  try {
                                    const token =
                                      sessionStorage.getItem("usr_1a2b3c");
                                    const axiosInstance = axios.create({
                                      baseURL: process.env.REACT_APP_API_URL,
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                      },
                                    });
                                    const res = await axiosInstance.post(
                                      `${APP_PATH.onsiteOrderWorkflow}/${orderId}/onsite-workflow`,
                                      { action: "collect_sample" },
                                    );
                                    if (res.data.status) {
                                      toast.success(
                                        "Sample marked as collected.",
                                      );
                                      fetchOrderDetails();
                                    } else {
                                      toast.error(
                                        res.data.message || "Failed to update.",
                                      );
                                    }
                                  } catch (e) {
                                    toast.error(
                                      "Failed to update. Please try again.",
                                    );
                                  } finally {
                                    setIsSampleCollecting(false);
                                  }
                                }}
                                disabled={
                                  isSampleCollected ||
                                  isJobDone ||
                                  isSampleCollecting
                                }
                              >
                                {isSampleCollecting ? (
                                  <span className={styles["btn-spinner"]} />
                                ) : (
                                  <Check size={16} />
                                )}{" "}
                                {isSampleCollected
                                  ? t(
                                      "lab.appointmentDetail.sampleCollectedDone",
                                      "Sample Collected ✓",
                                    )
                                  : t(
                                      "lab.appointmentDetail.sampleCollected",
                                      "Sample Collected",
                                    )}
                              </button>

                              {/* Mark as Completed / Mark as Collected (COD) */}
                              <button
                                className={`${styles["apt-register-btn"]} ${isJobDone ? styles["gray_colored"] : ""} py-2 px-3`}
                                onClick={async () => {
                                  if (isJobDone || isCompletingJob) return;
                                  const action = isCOD
                                    ? "complete_job_cod"
                                    : "complete_job";
                                  setIsCompletingJob(true);
                                  try {
                                    const token =
                                      sessionStorage.getItem("usr_1a2b3c");
                                    const axiosInstance = axios.create({
                                      baseURL: process.env.REACT_APP_API_URL,
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                      },
                                    });
                                    const res = await axiosInstance.post(
                                      `${APP_PATH.onsiteOrderWorkflow}/${orderId}/onsite-workflow`,
                                      { action },
                                    );
                                    if (res.data.status) {
                                      toast.success(
                                        isCOD
                                          ? "Cash collected and job marked as done."
                                          : "Job marked as completed.",
                                      );
                                      fetchOrderDetails();
                                    } else {
                                      toast.error(
                                        res.data.message || "Failed to update.",
                                      );
                                    }
                                  } catch (e) {
                                    toast.error(
                                      "Failed to update. Please try again.",
                                    );
                                  } finally {
                                    setIsCompletingJob(false);
                                  }
                                }}
                                disabled={
                                  !isSampleCollected ||
                                  isJobDone ||
                                  isCompletingJob
                                }
                              >
                                {isCompletingJob ? (
                                  <span className={styles["btn-spinner"]} />
                                ) : (
                                  <Check size={16} />
                                )}{" "}
                                {isJobDone
                                  ? isCOD
                                    ? t(
                                        "lab.appointmentDetail.cashCollectedJobDone",
                                        "Cash Collected & Job Done ✓",
                                      )
                                    : t(
                                        "lab.appointmentDetail.jobCompleted",
                                        "Job Completed ✓",
                                      )
                                  : isCOD
                                    ? t(
                                        "lab.appointmentDetail.markCollectedJobDone",
                                        "Mark as Collected / Job Done",
                                      )
                                    : t(
                                        "lab.appointmentDetail.markCompletedJobDone",
                                        "Mark as Completed / Job Done",
                                      )}
                              </button>
                            </div>
                          ) : (
                            /* HOME VISIT WORKFLOW: Technician Status + WhatsApp */
                            <>
                              <div
                                className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}
                              >
                                <h2>
                                  {t(
                                    "lab.appointmentDetail.technicianStatus",
                                    "Technician Status",
                                  )}
                                </h2>
                              </div>

                              {/* Status is informational, so it is a banner, not a button. */}
                              <div
                                role="status"
                                className={`${styles["tech-status-banner"]} ${
                                  technicianStatus.isPending
                                    ? styles["tech-status-banner-pending"]
                                    : styles["tech-status-banner-success"]
                                }`}
                              >
                                {technicianStatus.isPending ? (
                                  <FaInfoCircle size={16} aria-hidden="true" />
                                ) : (
                                  <Check size={16} aria-hidden="true" />
                                )}
                                <div>
                                  <strong>{technicianStatus.label}</strong>
                                  {!technicianStatus.isPending &&
                                    assignedTechnicianName && (
                                      <span
                                        className={
                                          styles["tech-status-banner-detail"]
                                        }
                                      >
                                        {assignedTechnicianName}
                                        {assignedTechnicianPhone
                                          ? ` (${assignedTechnicianPhone})`
                                          : ""}
                                      </span>
                                    )}
                                </div>
                              </div>

                              <div className={`${styles["text-end"]} mt-4`}>
                                <button
                                  className={styles["green-btn"]}
                                  onClick={handleAssignTechnician}
                                  // Disabled until a technician is picked; the red
                                  // "Please add a technician." hint under the dropdown
                                  // tells the officer why.
                                  disabled={isSendWhatsAppDisabled}
                                >
                                  {assigningTechnician ? (
                                    <>
                                      <span className={styles["btn-spinner"]} />{" "}
                                      {t(
                                        "lab.appointmentDetail.sendingPatientDetails",
                                        "Sending...",
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <FaLocationArrow />{" "}
                                      {orderDetails?.assignedTechnicianId
                                        ? t(
                                            "lab.appointmentDetail.technicianAlreadyAssigned",
                                            "Technician Already Assigned",
                                          )
                                        : t(
                                            "lab.appointmentDetail.sendPatientDetailsWhatsApp",
                                            "Send Patient Details via WhatsApp",
                                          )}
                                    </>
                                  )}
                                </button>
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "Results & Reports" && (
              <>
                <div className={`${styles["patient-tab-content"]} p-0`}>
                  <div
                    className={`${styles["white-card"]} m-0 position-relative`}
                  >
                    {/* Check if report is already sent */}
                    {orderDetails?.reportStatus === "SENT" && (
                      <div
                        className={styles["report-sent-notice"]}
                        style={{
                          backgroundColor: "#e8f5e8",
                          border: "1px solid #4caf50",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          marginBottom: "16px",
                          color: "#2e7d32",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <Check
                          size={16}
                          style={{
                            marginRight: "8px",
                            verticalAlign: "middle",
                          }}
                        />
                        {t(
                          "lab.appointmentDetail.reportSentNotice",
                          "Report has been sent to patient. Upload, sharing, and file deletion are disabled.",
                        )}
                      </div>
                    )}

                    <input
                      type="file"
                      id="file-upload"
                      className={styles["file-input"]}
                      accept=".pdf"
                      multiple
                      onChange={handleMultipleFileUpload}
                      disabled={
                        uploadingFile || orderDetails?.reportStatus === "SENT"
                      }
                    />
                    <label
                      className={`${styles["result-upload-card"]} ${uploadingFile ? styles["uploading"] : ""} ${orderDetails?.reportStatus === "SENT" ? styles["disabled"] : ""}`}
                      htmlFor="file-upload"
                      style={{
                        opacity:
                          orderDetails?.reportStatus === "SENT" ? 0.6 : 1,
                        cursor:
                          orderDetails?.reportStatus === "SENT"
                            ? "not-allowed"
                            : "pointer",
                        pointerEvents:
                          orderDetails?.reportStatus === "SENT"
                            ? "none"
                            : "auto",
                      }}
                    >
                      <h2>
                        {t(
                          "lab.appointmentDetail.uploadTestResults",
                          "Upload Test Results",
                        )}
                      </h2>
                      <p className={styles["result-subtitle"]}>
                        {orderDetails?.reportStatus === "SENT"
                          ? t(
                              "lab.appointmentDetail.uploadDisabledSubtitle",
                              "Report has been sent to patient. Upload is disabled.",
                            )
                          : t(
                              "lab.appointmentDetail.uploadResultsSubtitle",
                              "Upload PDF reports (single or multiple files, max 10 files, 25MB each). All files will be automatically available in the patient's app.",
                            )}
                      </p>

                      <span className={styles["result-upload-btn"]}>
                        <Upload size={20} />{" "}
                        {orderDetails?.reportStatus === "SENT"
                          ? t(
                              "lab.appointmentDetail.uploadDisabled",
                              "Upload Disabled",
                            )
                          : uploadingFile
                            ? t(
                                "lab.appointmentDetail.uploading",
                                "Uploading...",
                              )
                            : t("lab.appointmentDetail.upload", "Upload")}
                      </span>
                    </label>

                    <div className={`${styles["result-file-list"]} mt-4`}>
                      {/* Show uploaded files from order details */}
                      {orderDetails?.documents
                        ?.filter((doc) => doc.document_type === "TEST_RESULT")
                        .map((file, index) => (
                          <div
                            key={file.id || index}
                            className={styles["result-file-item"]}
                          >
                            <span className={styles["result-file-icon"]}>
                              <AiOutlineFilePdf size={20} color="#E55353" />
                            </span>
                            <span
                              className={`${styles["result-file-name"]} ${styles["clickable-file"]}`}
                              onClick={() =>
                                handleFileClick(file.id, file.file_name)
                              }
                              style={{
                                cursor: "pointer",
                                color: "#155DFC",
                                textDecoration: "underline",
                              }}
                            >
                              {file.file_name || `Test Result ${index + 1}.pdf`}
                            </span>
                            <button
                              className={styles["result-delete-btn"]}
                              onClick={() =>
                                handleFileDelete(file.id, file.file_name)
                              }
                              disabled={orderDetails?.reportStatus === "SENT"}
                              style={{
                                opacity:
                                  orderDetails?.reportStatus === "SENT"
                                    ? 0.4
                                    : 1,
                                cursor:
                                  orderDetails?.reportStatus === "SENT"
                                    ? "not-allowed"
                                    : "pointer",
                                backgroundColor:
                                  orderDetails?.reportStatus === "SENT"
                                    ? "#f5f5f5"
                                    : "",
                              }}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}

                      {/* Show uploaded files from current session */}
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={file.id || `uploaded-${index}`}
                          className={styles["result-file-item"]}
                        >
                          <span className={styles["result-file-icon"]}>
                            <AiOutlineFilePdf size={20} color="#E55353" />
                          </span>
                          <span
                            className={`${styles["result-file-name"]} ${styles["clickable-file"]}`}
                            onClick={() =>
                              handleFileClick(file.id, file.fileName)
                            }
                            style={{
                              cursor: "pointer",
                              color: "#155DFC",
                              textDecoration: "underline",
                            }}
                          >
                            {file.fileName}
                          </span>
                          <span className={styles["result-file-time"]}>
                            {tzDateTimeNumeric(new Date(file.uploadedAt))}
                          </span>
                          <button
                            className={styles["result-delete-btn"]}
                            onClick={() => {
                              // Remove from uploaded files list
                              setUploadedFiles((prev) =>
                                prev.filter((f) => f.id !== file.id),
                              );
                              toast.info("File removed from list");
                            }}
                            disabled={orderDetails?.reportStatus === "SENT"}
                            style={{
                              opacity:
                                orderDetails?.reportStatus === "SENT" ? 0.4 : 1,
                              cursor:
                                orderDetails?.reportStatus === "SENT"
                                  ? "not-allowed"
                                  : "pointer",
                              backgroundColor:
                                orderDetails?.reportStatus === "SENT"
                                  ? "#f5f5f5"
                                  : "",
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}

                      {/* Show message if no files uploaded */}
                      {(!orderDetails?.documents ||
                        orderDetails.documents.filter(
                          (doc) => doc.document_type === "TEST_RESULT",
                        ).length === 0) &&
                        uploadedFiles.length === 0 && (
                          <div className={styles["no-files-message"]}>
                            <p>
                              {t(
                                "lab.appointmentDetail.noTestResults",
                                "No test results uploaded yet.",
                              )}
                            </p>
                          </div>
                        )}
                    </div>

                    <div className={styles["result-actions"]}>
                      <button
                        className={styles["result-share-btn"]}
                        onClick={handleShareWithPatient}
                        disabled={orderDetails?.reportStatus === "SENT"}
                        style={{
                          opacity:
                            orderDetails?.reportStatus === "SENT" ? 0.6 : 1,
                          cursor:
                            orderDetails?.reportStatus === "SENT"
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            orderDetails?.reportStatus === "SENT" ? "#ccc" : "",
                        }}
                      >
                        <Check size={16} />{" "}
                        {orderDetails?.reportStatus === "SENT"
                          ? t(
                              "lab.appointmentDetail.alreadyShared",
                              "Already Shared",
                            )
                          : t(
                              "lab.appointmentDetail.shareWithPatient",
                              "Share with Patient & Doctor",
                            )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Shared invoice modal — identical layout + fee math everywhere. */}
      {showInvoice && (
        <LabInvoiceModal
          open={showInvoice}
          onClose={() => setShowInvoice(false)}
          order={orderDetails}
          centerProfile={labCenterProfile}
          adminFeePercentage={adminFeePercentage}
        />
      )}

      {/* Accept Order Confirmation Modal — styled like the Add New Test dialog */}
      {showAcceptModal && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={() => setShowAcceptModal(false)}
                disabled={acceptingOrder}
              >
                ×
              </button>
              <h2 className={dialogStyles["dialog-title"]}>
                {t("lab.appointmentDetail.acceptOrderTitle", "Accept Order")}
              </h2>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "lab.appointmentDetail.acceptOrderMessage",
                  "Accept this order? The patient will be notified and you can then add tests and send the invoice.",
                )}
              </p>
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={() => setShowAcceptModal(false)}
                  disabled={acceptingOrder}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  {t("lab.appointmentDetail.cancel", "Cancel")}
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmAcceptOrder}
                  disabled={acceptingOrder}
                  style={{
                    opacity: acceptingOrder ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <Check size={16} />{" "}
                  {acceptingOrder
                    ? t("lab.appointmentDetail.accepting", "Accepting...")
                    : t("lab.appointmentDetail.accept", "Accept")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Order Confirmation Modal — styled like the Add New Test dialog */}
      {showRejectModal && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={() => {
                  setShowRejectModal(false);
                  resetRejectForm();
                }}
                disabled={rejectingOrder}
              >
                ×
              </button>
              <h2 className={dialogStyles["dialog-title"]}>
                {t("lab.appointmentDetail.rejectOrderTitle", "Reject Order")}
              </h2>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "lab.appointmentDetail.rejectOrderMessage",
                  "Reject this order? The patient will be notified. This will move the order to the Rejected tab.",
                )}
              </p>
              <label
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  fontWeight: 500,
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                {t("lab.appointmentDetail.reasonOptional", "Reason (optional)")}
              </label>
              {/* Admin-configured reasons for this center + an "Other" escape
                  hatch that reveals the free-text box below. */}
              <select
                value={selectedRejectReasonId}
                onChange={(e) => {
                  setSelectedRejectReasonId(e.target.value);
                  // Drop any half-typed custom text when switching to a preset.
                  if (e.target.value !== CUSTOM_REJECTION_REASON)
                    setRejectReason("");
                }}
                disabled={rejectingOrder || loadingRejectReasons}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  marginBottom: isCustomRejectReason ? "10px" : "4px",
                  boxSizing: "border-box",
                  backgroundColor: "#fff",
                  cursor: rejectingOrder ? "not-allowed" : "pointer",
                }}
              >
                <option value="">
                  {loadingRejectReasons
                    ? t("lab.appointmentDetail.loadingReasons", "Loading...")
                    : t(
                        "lab.appointmentDetail.selectReason",
                        "Select a reason (optional)",
                      )}
                </option>
                {rejectReasonOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value={CUSTOM_REJECTION_REASON}>
                  {t(
                    "lab.appointmentDetail.otherReason",
                    "Other (type your own reason)",
                  )}
                </option>
              </select>
              {isCustomRejectReason && (
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t(
                    "lab.appointmentDetail.reasonPlaceholder",
                    "Add a reason for rejecting this order...",
                  )}
                  rows={3}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    resize: "vertical",
                    marginBottom: "4px",
                    boxSizing: "border-box",
                  }}
                />
              )}
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={() => {
                    setShowRejectModal(false);
                    resetRejectForm();
                  }}
                  disabled={rejectingOrder}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  {t("lab.appointmentDetail.cancel", "Cancel")}
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmRejectOrder}
                  disabled={rejectingOrder}
                  style={{
                    opacity: rejectingOrder ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  {rejectingOrder
                    ? t("lab.appointmentDetail.rejecting", "Rejecting...")
                    : t("lab.appointmentDetail.reject", "Reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LabDashboardLayout>
  );
};

export default LabAppointmentDetail;
